import { crmEn as crm } from './crm';
/** English translations (secondary language, kept structurally identical to `fr.ts`). */

export const common = {
  appName: 'HOREVIA',
  actions: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    edit: 'Edit',
    delete: 'Delete',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    retry: 'Retry',
    reset: 'Reset',
    back: 'Back',
    loadMore: 'Load more',
    copy: 'Copy',
    copied: 'Copied',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    openMenu: 'Open menu',
    clearFilters: 'Clear filters',
    remove: 'Remove',
    actions: 'Actions',
    yes: 'Yes',
    no: 'No',
  },
  states: {
    loading: 'Loading…',
    empty: 'No results',
    errorTitle: 'Something went wrong',
    errorDescription:
      'The operation could not be completed. Try again; if the problem persists, send the trace identifier to support.',
    offline: 'Offline — showing the last known data.',
    noPermissionTitle: 'Access denied',
    noPermissionDescription:
      'You do not have access to this section. Ask your establishment administrator for the required rights.',
    notFoundTitle: 'Page not found',
    notFoundDescription: 'This address does not exist or the item has been removed.',
    moduleUnavailableTitle: 'Module not enabled',
    moduleUnavailableDescription:
      'The {{module}} module is not enabled for your organisation. It appears neither in the navigation nor in the screens.',
    comingSoonTitle: 'Coming soon',
    comingSoonDescription:
      'This module is part of the MVP scope and will ship in a later phase. Navigation is already in place.',
    traceId: 'Trace identifier: {{traceId}}',
  },
  fields: {
    required: 'required',
    optional: 'optional',
    email: 'Email address',
    password: 'Password',
    fullName: 'Full name',
    phone: 'Phone',
    language: 'Language',
    status: 'Status',
    createdAt: 'Created on',
    updatedAt: 'Updated on',
  },
  languages: { fr: 'French', en: 'English' },
  country: {
    placeholder: 'Choose a country',
    search: 'Search a country…',
    empty: 'No country matches.',
    nearby: 'Burkina Faso and neighbouring countries',
    all: 'All countries',
  },
  theme: { toggle: 'Toggle light / dark theme', light: 'Light', dark: 'Dark' },
  pagination: {
    showing: '{{count}} item shown',
    showing_other: '{{count}} items shown',
    loadMore: 'Load more',
  },
  validation: {
    required: 'This field is required.',
    email: 'Invalid email address.',
    minLength: 'At least {{count}} characters.',
    passwordPolicy: 'The password must contain at least 12 characters.',
    passwordMismatch: 'The two passwords do not match.',
    phone: 'International format, for example +226 78 82 32 38.',
    code6: 'The code has 6 digits.',
  },
};

export const nav = {
  mainLabel: 'Main navigation',
  skipToContent: 'Skip to main content',
  home: 'HOREVIA — dashboard',
  collapse: 'Collapse menu',
  expand: 'Expand menu',
  toggleMenu: 'Show or hide the menu',
  search: 'Search (⌘K)',
  notifications: 'Notifications',
  notificationsCount: 'Notifications ({{count}} unread)',
  property: 'Selected property',
  propertySwitch: 'Switch establishment',
  profileMenu: 'Account menu',
  items: {
    dashboard: 'Dashboard',
    reservations: 'Reservations',
    reservationsCalendar: 'Calendar',
    reservationsList: 'Reservations',
    reservationsNew: 'New reservation',
    customers: 'Customers',
    customersPeople: 'People',
    customersCompanies: 'Companies',
    resources: 'Accommodation & rooms',
    resourcesList: 'Resources',
    resourcesAvailability: 'Availability',
    resourcesMaintenance: 'Maintenance',
    operations: 'Operations',
    operationsArrivals: 'Arrivals',
    operationsDepartures: 'Departures',
    operationsHousekeeping: 'Housekeeping',
    operationsTasks: 'Tasks',
    billing: 'Billing',
    billingProformas: 'Proformas',
    billingInvoices: 'Invoices',
    billingPayments: 'Payments',
    billingCash: 'Cash desk',
    billingExpenses: 'Expenses',
    restaurant: 'Restaurant · Bar',
    restaurantPos: 'Point of sale',
    restaurantOrders: 'Orders',
    restaurantKitchen: 'Kitchen',
    restaurantTabs: 'Counter',
    restaurantTables: 'Floor plan',
    restaurantCatalog: 'Menu',
    restaurantOutlets: 'Outlets & printing',
    stock: 'Stock',
    stockLevels: 'Levels & alerts',
    stockMovements: 'Movements',
    stockPurchases: 'Purchases',
    stockCounts: 'Stock counts',
    stockItems: 'Items & locations',
    reports: 'Reports',
    administration: 'Administration',
    adminUsers: 'Users',
    adminRoles: 'Roles',
    adminTaxes: 'Taxes',
    adminSettings: 'Property & settings',
    adminAudit: 'Audit log',
    platform: 'Platform',
    platformTenants: 'Tenants',
    platformBilling: 'Billing',
    platformPricing: 'Pricing',
    adminSupport: 'Support access',
    adminSetup: 'Getting started',
    analytics: 'Analytics',
    analyticsOverview: 'Consolidated view',
    analyticsSales: 'Sales',
    analyticsStock: 'Stock & margins',
    crm: 'CRM',
    crmOverview: 'Overview',
    crmCustomers: '360° customers',
    crmFollowUps: 'Follow-ups',
    crmSegments: 'Segments',
    crmLoyalty: 'Loyalty',
    crmDuplicates: 'Duplicates',
    crmSettings: 'CRM settings',
  },
  palette: {
    actionCreateTenant: 'New tenant',
    actionRecordPayment: 'Record a payment',
    actionOpenCash: 'Open the cash drawer',
    actionRecordExpense: 'New expense',
    actionSearchPayment: 'Search a payment',
    title: 'Command palette',
    placeholder: 'Search a page, an action…',
    empty: 'No result for this search.',
    groupNavigation: 'Navigation',
    groupActions: 'Actions',
    groupResults: 'Results',
    groupPreferences: 'Preferences',
    actionInviteUser: 'Invite a user',
    actionCreateRole: 'Create a role',
    actionCreateReservation: 'New reservation',
    actionCreateCustomer: 'New customer',
    actionCreateResource: 'New resource',
    actionCreateBlock: 'New block',
    actionOpenCalendar: 'Open the calendar',
    actionSearchAvailability: 'Search availability',
    actionCreateProforma: 'New proforma',
    actionSearchInvoice: 'Search an invoice (FAC/…)',
    actionOpenReports: 'View reports',
    actionExportReport: 'Export report…',
    actionExportReportHint: 'CSV',
    shortcutNavigate: 'navigate',
    shortcutOpen: 'open',
    shortcutClose: 'close',
    actionToggleTheme: 'Toggle light / dark theme',
    actionLogout: 'Sign out',
    soon: 'soon',
  },
  profile: { myProfile: 'My profile', security: 'Security', logout: 'Sign out' },
};

export const auth = {
  login: {
    title: 'Sign in',
    subtitle: 'Access the management of your establishment.',
    email: 'Email address',
    emailPlaceholder: 'firstname.lastname@example.com',
    password: 'Password',
    forgot: 'Forgot your password?',
    submit: 'Sign in',
    mfaNoticeTitle: 'Two-factor authentication enabled',
    mfaNoticeBody:
      'A 6-digit code from your authenticator app (TOTP) will be requested after the password check.',
    footer: 'HOREVIA — The digital path of hospitality',
    sessionExpired: 'Your session has expired. Sign in again to continue.',
  },
  mfa: {
    title: 'Two-step verification',
    subtitle: 'Enter the 6-digit code shown by your authenticator app.',
    codeLabel: 'Verification code',
    submit: 'Verify',
    backToLogin: 'Back to sign in',
  },
  forgot: {
    title: 'Forgot password',
    subtitle: 'Enter your work address: if an account exists, a reset link will be sent to you.',
    submit: 'Send the link',
    sentTitle: 'Check your inbox',
    sentBody: 'If an account matches {{email}}, a reset link valid for 1 hour has just been sent.',
    backToLogin: 'Back to sign in',
  },
  reset: {
    title: 'New password',
    subtitle: 'Choose a password of at least 12 characters, unique to HOREVIA.',
    password: 'New password',
    confirm: 'Confirm password',
    submit: 'Save the password',
    successTitle: 'Password changed',
    successBody: 'You can now sign in with your new password.',
    invalidToken: 'This reset link is invalid or has expired. Request a new one.',
  },
  invitation: {
    title: 'Activate your account',
    subtitle: 'You are joining {{tenant}} as {{roles}}.',
    fullName: 'Full name',
    password: 'Password',
    confirm: 'Confirm password',
    submit: 'Activate my account',
    invalid: 'This invitation is invalid, expired or already used.',
    successTitle: 'Account activated',
    successBody: 'Sign in with your work address and your new password.',
  },
  brand: {
    badge: 'Hospitality platform',
    headline: 'The stay, the compliance and the invoice, in one place.',
    lead: 'Hotel, restaurant, bar and point of sale: one modular platform built for hospitality professionals.',
    promise1Title: 'Real-time planning and availability',
    promise1Body: 'Apartments and meeting room on a single timeline: no more double booking.',
    promise2Title: 'Compliance checked before invoicing',
    promise2Body:
      'ID card or passport and completed police form checked: the final invoice never goes out incomplete.',
    promise3Title: 'Compliant proformas and invoices',
    promise3Body: 'Gapless numbering, RCCM / IFU / CME statements, amount in words, immutable PDF.',
    mockToday: 'Today',
    mockArrivals: 'Arrivals',
    mockDepartures: 'Departures',
    mockOccupancy: 'Occupancy',
  },
  errors: {
    INVALID_CREDENTIALS: 'Incorrect email address or password.',
    ACCOUNT_LOCKED:
      'Account temporarily locked after several attempts. Try again in a few minutes.',
    ACCOUNT_DISABLED: 'This account is disabled. Contact your establishment administrator.',
    MFA_INVALID: 'Incorrect or expired verification code.',
    RATE_LIMITED: 'Too many attempts. Wait before trying again.',
  },
};

export const dashboard = {
  greeting: 'Hello {{name}}',
  subtitle: 'Here is the activity of your residence for {{date}}.',
  todayTitle: 'Today',
  today: 'Today',
  operations: 'Operations',
  seeAll: 'See all',
  scheduled: 'scheduled',
  balance: 'Balance {{amount}}',
  incomplete: 'Incomplete file',
  incompleteTooltip: 'ID document or police form missing',
  incompleteFoot: 'final invoice blocked (R-001)',
  inHouseFoot: 'ongoing stays on the property',
  collect: 'Collect',
  collectHint: 'Open the reservation to collect the balance (Payments tab).',
  expectedPayments: 'Expected payments',
  expectedPaymentsFoot: 'balances of today’s arrivals and departures',
  metrics: {
    arrivals: 'Expected arrivals',
    departures: 'Expected departures',
    inHouse: 'In house',
    incompleteCompliance: 'Incomplete files',
    availableResources: 'Free units & rooms',
    expectedPayments: 'Expected payments',
    collectedToday: 'Collected today',
    cash: 'Cash drawer',
    cashOpen: 'Open',
    cashClosed: 'Closed',
  },
  arrivalsCard: "Today's arrivals",
  departuresCard: "Today's departures",
  inHouseCard: 'Ongoing stays',
  arrivalsTitle: "Today's arrivals",
  arrivalsSubtitle: 'Arrivals expected today, ready for check-in.',
  departuresTitle: "Today's departures",
  departuresSubtitle: 'Departures expected today, with their outstanding balance.',
  arrivalsEmptyTitle: 'No arrival today',
  arrivalsEmptyBody: 'No confirmed reservation starts today.',
  departuresEmptyTitle: 'No departure today',
  departuresEmptyBody: 'No ongoing stay ends today.',
  inHouseEmptyTitle: 'No ongoing stay',
  inHouseEmptyBody: 'No guest is currently on the property.',
};

export const users = {
  title: 'Users',
  subtitle: 'Your organisation’s accounts: roles, scope, two-factor authentication and sessions.',
  invite: 'Invite a user',
  searchPlaceholder: 'Search a user',
  searchLabel: 'Search a user by name or email',
  kpi: {
    active: 'Active users',
    activeFoot: '{{count}} pending invitation',
    activeFoot_other: '{{count}} pending invitations',
    mfa: 'Two-factor authentication',
    mfaFoot: '{{count}} account to secure',
    mfaFoot_other: '{{count}} accounts to secure',
    roles: 'Configured roles',
    rolesFoot: 'including {{count}} custom role',
    rolesFoot_other: 'including {{count}} custom roles',
    disabled: 'Disabled accounts',
    disabledFoot: 'access revoked, history kept',
  },
  columns: {
    user: 'User',
    roles: 'Roles',
    scope: 'Scope',
    mfa: 'MFA',
    lastLogin: 'Last sign-in',
    status: 'Status',
    actions: 'Actions',
  },
  status: { INVITED: 'Invited', ACTIVE: 'Active', DISABLED: 'Disabled' },
  filters: { all: 'All', status: 'Status', role: 'Role' },
  mfaEnabled: 'Enabled',
  mfaDisabled: 'To enable',
  allProperties: 'All sites',
  never: 'Never',
  emptyTitle: 'No user',
  emptyBody: 'Invite a colleague to give them access to HOREVIA.',
  emptySearchTitle: 'No matching user',
  emptySearchBody: 'Change the search or clear the filters.',
  drawer: {
    inviteTitle: 'Invite a user',
    inviteSubtitle:
      'The activation link will be shown to you on screen (and emailed if mail is configured). The account stays “Invited” until it is activated.',
    editTitle: 'Edit {{name}}',
    editSubtitle: 'Name, language and assigned roles.',
    rolesLabel: 'Roles',
    rolesHint: 'At least one role. Per-property scope will be handled in V1 (multi-site).',
    submitInvite: 'Create the invitation',
    submitEdit: 'Save changes',
  },
  detail: {
    title: 'User record',
    profile: 'Profile',
    rolesCard: 'Roles and permissions',
    sessionsCard: 'Active sessions',
    securityCard: 'Security',
    dangerCard: 'Sensitive area',
    sessionDevice: 'Device',
    sessionIp: 'IP address',
    sessionLastSeen: 'Last activity',
    sessionCurrent: 'Current session',
    noSessions: 'No active session.',
    permissionsCount: '{{count}} effective permission',
    permissionsCount_other: '{{count}} effective permissions',
  },
  actions: {
    edit: 'Edit',
    deactivate: 'Disable account',
    activate: 'Re-enable account',
    resetPassword: 'Reset password',
    revokeSessions: 'Revoke sessions',
    resendInvitation: 'Resend invitation',
  },
  confirm: {
    deactivateTitle: 'Disable {{name}}?',
    deactivateBody:
      'The user will immediately lose access and their sessions will be closed. History and audit are kept.',
    deactivateConfirm: 'Disable account',
    resetTitle: 'Reset the password of {{name}}?',
    resetBody:
      'A reset link valid for 30 minutes will be shown to you on screen (and emailed if mail is configured). The user’s sessions stay open.',
    resetConfirm: 'Generate the link',
    resendTitle: 'Resend the invitation to {{name}}?',
    resendBody:
      'A new activation link valid for 7 days will be generated; the previous link will stop working.',
    resendConfirm: 'Generate the link',
    revokeTitle: 'Revoke the sessions of {{name}}?',
    revokeBody:
      'All open sessions will be closed; the user will need to sign in again. The action is logged.',
    revokeConfirm: 'Revoke sessions',
  },
  toasts: {
    invited: 'Invitation sent to {{email}}.',
    invitedNoEmail: 'Account for {{email}} created. Pass on the activation link.',
    updated: 'User updated.',
    deactivated: 'Account disabled.',
    activated: 'Account re-enabled.',
    passwordReset: 'Reset link sent by email.',
    passwordResetNoEmail: 'Reset link generated. Pass it on yourself.',
    sessionsRevoked: 'Sessions revoked.',
    invitationResent: 'Invitation resent by email.',
    invitationResentNoEmail: 'New activation link generated. Pass it on yourself.',
  },
  deliveryLink: {
    invitationTitle: 'Activation link',
    resetTitle: 'Reset link',
    recipient: 'For {{name}} ({{email}})',
    field: 'Link to pass on',
    expiresOn: 'expires on {{date}} at {{time}}',
    validFor: {
      minutes: 'Valid for {{count}} minute ({{expiry}}).',
      minutes_other: 'Valid for {{count}} minutes ({{expiry}}).',
      hours: 'Valid for {{count}} hour ({{expiry}}).',
      hours_other: 'Valid for {{count}} hours ({{expiry}}).',
      days: 'Valid for {{count}} day ({{expiry}}).',
      days_other: 'Valid for {{count}} days ({{expiry}}).',
    },
    expired: 'Link expired ({{expiry}}). Generate a new one.',
    copiedAnnouncement: 'Link copied to the clipboard.',
    manualCopy:
      'Automatic copy unavailable: the link is selected, copy it with Ctrl+C (Cmd+C on Mac).',
    emailSent: 'An email containing this link was also sent to the recipient.',
    noEmailTitle: 'No email sent (mail not configured)',
    noEmailBody:
      'Pass this link on yourself, through a safe channel (WhatsApp, in person). Do not send it to anyone else: it grants access to the account.',
  },
};

export const roles = {
  title: 'Roles and permissions',
  subtitle:
    'Read-only system roles and tenant custom roles. The server remains the sole authority on authorisation.',
  create: 'Create a role',
  columns: {
    role: 'Role',
    code: 'Code',
    users: 'Users',
    permissions: 'Permissions',
    type: 'Type',
    actions: 'Actions',
  },
  system: 'System role',
  custom: 'Custom',
  readOnlyNotice:
    'This system role ships with the product: its permissions are read-only. Duplicate it to create a variant.',
  usersCount: '{{count}} user',
  usersCount_other: '{{count}} users',
  permissionsCount: '{{count}} permission',
  permissionsCount_other: '{{count}} permissions',
  drawer: {
    createTitle: 'Create a role',
    editTitle: 'Edit {{name}}',
    viewTitle: '{{name}}',
    name: 'Role name',
    code: 'Technical code',
    codeHint: 'Lowercase, digits and underscores; cannot be changed after creation.',
    description: 'Description',
    permissions: 'Permissions',
    permissionsHint:
      'Tick the allowed actions. Permissions flagged “sensitive” always emit an audit event.',
    searchPermission: 'Filter permissions',
    selectedCount: '{{count}} permission selected',
    selectedCount_other: '{{count}} permissions selected',
    selectAllDomain: 'Select all',
    clearDomain: 'Clear all',
    submitCreate: 'Create the role',
    submitEdit: 'Save',
  },
  sensitive: 'sensitive',
  highPrivilege: 'high privilege',
  domains: {
    tenant: 'Tenant and properties',
    settings: 'Settings',
    user: 'Users and roles',
    resource: 'Resources and rates',
    reservation: 'Reservations',
    customer: 'Customers',
    compliance: 'Compliance',
    document: 'Documents',
    proforma: 'Proformas',
    invoice: 'Invoices',
    payment: 'Payments',
    cash: 'Cash desk',
    housekeeping: 'Housekeeping',
    maintenance: 'Maintenance',
    notification: 'Notifications',
    report: 'Reports',
    audit: 'Audit log',
    platform: 'Platform',
  },
  systemRoles: {
    owner: 'Manager (owner)',
    manager: 'Manager',
    front_desk: 'Front desk',
    accountant: 'Accounting',
    housekeeping: 'Housekeeping',
    waiter: 'Waiter',
    cashier: 'Cashier',
    kitchen: 'Kitchen',
    read_only: 'Read only',
    platform_support: 'Platform support',
  },
  toasts: { created: 'Role created.', updated: 'Role updated.', deleted: 'Role deleted.' },
  emptyTitle: 'No custom role',
  emptyBody: 'Create a role to fine-tune your team’s permissions.',
};

export const audit = {
  title: 'Audit log',
  subtitle:
    'Append-only traceability of sensitive actions: who, what, when, before and after. No line can be modified or deleted.',
  columns: {
    occurredAt: 'Date',
    actor: 'Actor',
    action: 'Action',
    entity: 'Entity',
    ip: 'IP',
    trace: 'Trace',
  },
  filters: {
    action: 'Action',
    entityType: 'Entity type',
    actor: 'Actor',
    from: 'From',
    to: 'To',
    allActions: 'All actions',
    allEntities: 'All entities',
    allActors: 'All actors',
    actorType: 'Actor type',
    allActorTypes: 'All actor types',
  },
  actorSystem: 'System',
  actorPlatform: 'Platform',
  actorTypes: { USER: 'User', SYSTEM: 'System', PLATFORM: 'Platform' },
  detailTitle: 'Event detail',
  before: 'Before',
  after: 'After',
  noDiff: 'No detailed data for this event.',
  reason: 'Reason',
  emptyTitle: 'No event',
  emptyBody: 'No action matches the selected filters.',
  actions: {
    'auth.login': 'Sign-in',
    'auth.login_failed': 'Failed sign-in',
    'auth.logout': 'Sign-out',
    'user.invite': 'User invited',
    'user.update': 'User updated',
    'user.deactivate': 'Account disabled',
    'role.create': 'Role created',
    'role.update': 'Role updated',
    'role.delete': 'Role deleted',
    'role.assign': 'Role assigned',
    'tenant.update': 'Tenant record updated',
    'property.create': 'Property created',
    'property.update': 'Property updated',
    'session.revoke': 'Sessions revoked',
    'mfa.enable': 'Two-factor authentication enabled',
    'mfa.disable': 'Two-factor authentication disabled',
    'password.reset': 'Password reset',
    'support.access.requested': 'Support access requested',
    'support.access.approved': 'Support access approved',
    'support.access.declined': 'Support access declined',
    'support.access.cancelled': 'Support access request withdrawn',
    'support.access.entered': 'Support entered (read only)',
    'support.access.left': 'Support left',
    'support.access.revoked': 'Support access revoked',
    'support.access.expired': 'Support access expired',
  },
};

export const settings = {
  title: 'Property & settings',
  subtitle:
    'Company identity, legal statements on documents, bank details, branding and property opening hours.',
  tabs: {
    identity: 'Identity',
    legal: 'Legal',
    bank: 'Bank',
    branding: 'Branding',
    properties: 'Properties',
    resourceCatalog: 'Resource types & amenities',
    compliance: 'Compliance',
    billing: 'Billing',
    modules: 'Modules',
    subscription: 'Subscription',
  },
  modules: {
    subscriptionCard: 'Subscription',
    planNotice:
      'Modules included in your subscription are enabled for the whole organisation; you can disable them per establishment. To add a module, contact HOREVIA.',
    noSubscription: 'No active subscription: business modules are unavailable. Contact HOREVIA.',
    since: 'Since {{date}}',
    dependsOn: 'Requires: {{modules}}',
    toggleFor: '{{module}} for {{property}}',
    goToSettings: 'See modules',
    status: {
      TRIAL: 'Trial',
      ACTIVE: 'Active',
      PAST_DUE: 'Past due',
      SUSPENDED: 'Suspended',
      CANCELLED: 'Cancelled',
    },
    state: {
      enabled: 'Enabled',
      inPlan: 'Included, not enabled',
      notSubscribed: 'Not subscribed',
      comingSoon: 'Coming soon',
    },
    names: {
      hotel: 'Horevia Hotel',
      restaurant: 'Horevia Restaurant',
      bar: 'Horevia Bar',
      pos: 'Horevia POS',
      inventory: 'Horevia Inventory',
      crm: 'Horevia CRM',
      analytics: 'Horevia Analytics',
      housekeeping: 'Horevia Housekeeping',
      booking: 'Horevia Booking',
      pay: 'Horevia Pay',
    },
    toasts: {
      enabled: 'Module enabled for the establishment.',
      disabled: 'Module disabled for the establishment.',
    },
  },
  identity: {
    card: 'Company identity',
    legalName: 'Legal name',
    tradeName: 'Trade name',
    country: 'Country',
    currency: 'Currency',
    timezone: 'Time zone',
    locale: 'Default language',
    email: 'Contact email',
    phone: 'Phone',
    website: 'Website',
    addressLine1: 'Address',
    addressLine2: 'Address line 2',
    poBox: 'PO box',
    city: 'City',
  },
  legal: {
    card: 'Legal statements on invoices',
    hint: 'These details are frozen into each invoice when it is issued (issuer snapshot).',
    rccm: 'RCCM',
    ifu: 'IFU',
    taxRegime: 'Tax regime',
    taxDivision: 'Tax division',
    legalForm: 'Legal form',
    activity: 'Business activity',
    activityHint:
      'Line shown under the business name at the top of invoices, proformas, credit notes and receipts.',
  },
  bank: {
    card: 'Bank details',
    bankName: 'Bank',
    accountNumber: 'Account number',
    iban: 'IBAN',
    swift: 'SWIFT / BIC code',
  },
  branding: {
    card: 'Branding and documents',
    primaryColor: 'Primary colour',
    secondaryColor: 'Secondary colour',
    documentSignatory: 'Document signatory',
    logo: {
      card: 'Document logo',
      description:
        'Shown at the top of invoices, proformas, credit notes, receipts, POS tickets and booking confirmations. Without a logo, only the business name appears.',
      preview: 'Tenant logo',
      none: 'No logo uploaded.',
      dropTitle: 'Upload a logo',
      dropHint: 'PNG, JPEG or WebP · 512 KB max · 16 to 4000 px per side',
      browse: 'Choose a file',
      remove: 'Remove logo',
      removeConfirm: 'Documents rendered from now on will carry no logo. Continue?',
    },
  },
  properties: {
    card: 'Properties',
    name: 'Name',
    code: 'Code',
    checkinFrom: 'Check-in from',
    checkoutUntil: 'Check-out until',
    timezone: 'Time zone',
    active: 'Active',
    inactive: 'Inactive',
    hint: 'Decision D-021: check-in from 12:00, check-out by 12:00 at the latest.',
    edit: 'Edit property',
    activities: 'Activities',
    activity: {
      HOTEL: 'Hotel',
      RESTAURANT: 'Restaurant',
      BAR: 'Bar',
      OTHER: 'Other',
    },
  },
  toasts: {
    tenantUpdated: 'Tenant record saved.',
    propertyUpdated: 'Property saved.',
    logoUpdated: 'Logo saved.',
    logoRemoved: 'Logo removed.',
  },
  readOnlyNotice: 'You are viewing these settings in read-only mode.',
};

export const profile = {
  title: 'My profile',
  subtitle: 'Your details, your security and your sessions.',
  identityCard: 'Personal information',
  passwordCard: 'Password',
  mfaCard: 'Two-factor authentication',
  notifications: {
    title: 'Notifications',
    body: 'Pick, for each topic, the channels that alert you. The internal channel feeds the top-bar bell; the e-mail channel sends a message.',
    caption: 'Notification channels per topic',
    topic: 'Topic',
    internal: 'Internal',
    email: 'E-mail',
    internalAria: 'Internal notification — {{topic}}',
    emailAria: 'E-mail notification — {{topic}}',
    digestHour: 'Daily digest hour',
    saved: 'Notification preferences saved.',
    topics: {
      RESERVATION_TO_CONFIRM: 'Reservation to confirm',
      OPTION_EXPIRING: 'Option expiring',
      ARRIVAL_TODAY: 'Arrival today',
      DEPARTURE_TODAY: 'Departure today',
      DOCUMENT_MISSING: 'Missing compliance document',
      PAYMENT_OVERDUE: 'Overdue payment',
      INVOICE_UNPAID: 'Unpaid invoice',
      RESERVATION_CANCELLED: 'Reservation cancelled',
      MAINTENANCE: 'Maintenance and incidents',
      CASH_VARIANCE: 'Cash variance',
      DAILY_DIGEST: 'Daily digest',
      SUPPORT_ACCESS_REQUESTED: 'Support access request',
      SUBSCRIPTION_INVOICE: 'HOREVIA subscription invoice',
      SUBSCRIPTION_OVERDUE: 'HOREVIA subscription overdue',
    },
  },
  sessionsCard: 'Active sessions',
  currentPassword: 'Current password',
  newPassword: 'New password',
  confirmPassword: 'Confirm the new password',
  changePassword: 'Change password',
  mfaEnabled: 'Enabled',
  mfaDisabled: 'Disabled',
  mfaEnabledBody: 'A 6-digit code from your authenticator app is required at every sign-in.',
  mfaDisabledBody:
    'Strengthen your account: two-factor authentication is recommended for the Manager and Accounting roles.',
  enableMfa: 'Enable two-factor authentication',
  disableMfa: 'Disable',
  mfaSetupTitle: 'Enable two-factor authentication',
  mfaSetupStep1: '1. Scan this QR code with your authenticator app (TOTP).',
  mfaSetupStep2: '2. Enter the 6-digit code displayed to confirm.',
  mfaSecret: 'Backup key (if scanning is not possible)',
  mfaDisableTitle: 'Disable two-factor authentication',
  mfaDisableBody: 'Confirm with your password and a valid code. This action is logged.',
  revokeOthers: 'Sign out the other sessions',
  device: 'Device',
  ip: 'IP address',
  lastSeen: 'Last activity',
  current: 'Current session',
  toasts: {
    passwordChanged: 'Password changed.',
    mfaEnabled: 'Two-factor authentication enabled.',
    mfaDisabled: 'Two-factor authentication disabled.',
    sessionRevoked: 'Session signed out.',
    othersRevoked: 'The other sessions have been signed out.',
    profileUpdated: 'Profile updated.',
  },
};

export const resources = {
  title: 'Resources',
  subtitle:
    'Lodgings and meeting spaces of the establishment: inventory, rates, blocks and composition.',
  create: 'New resource',
  searchLabel: 'Search a resource',
  searchPlaceholder: 'Name or reference (110, F3…)',
  countLabel: '{{count}} resource',
  countLabel_other: '{{count}} resources',
  subCountLabel: '{{count}} sub-resource',
  subCountLabel_other: '{{count}} sub-resources',
  filters: {
    all: 'All',
    allTypes: 'All types',
    property: 'Property',
    type: 'Type',
    showInactive: 'Show archived resources',
    active: 'Active',
    archived: 'Archived',
  },
  kpi: {
    total: 'Active resources',
    totalFoot: 'including {{count}} sub-resource',
    totalFoot_other: 'including {{count}} sub-resources',
    capacity: 'Total capacity',
    capacityFoot: 'guests at the same time',
    blocked: 'Unavailable',
    blockedFoot: 'maintenance or block in progress',
    averageRate: 'Average rate / night',
    averageRateFoot: 'across active lodgings',
  },
  card: {
    capacity: '{{standard}} guests (max. {{max}})',
    floorGround: 'Ground floor',
    floorFirst: '1st floor',
    floorOther: 'Floor {{count}}',
    noRate: 'No default rate',
    parent: 'Parent resource',
    child: 'Sub-resource',
    childOf: 'Sub-resource of {{name}}',
    archived: 'Archived',
    open: 'Open the resource',
    rooms: '{{count}} bedroom',
    rooms_other: '{{count}} bedrooms',
  },
  emptyTitle: 'No resource yet',
  emptyBody:
    'Create the rooms, apartments and venues of the establishment to feed the calendar and reservations.',
  emptySearchTitle: 'No matching resource',
  emptySearchBody: 'Change the search or clear the filters to browse the inventory again.',
  compositeNotice: {
    title: 'F3 apartments and bedrooms are linked resources',
    body: 'Apartments F3 – 112 and F3 – 212 can be booked as a whole or bedroom by bedroom. The two options exclude each other: booking the whole apartment makes its bedrooms unavailable, and booking a single bedroom makes the whole apartment unavailable. The rule is enforced server-side (ADR-013).',
  },
  status: {
    label: 'Operational status',
    change: 'Change status',
    AVAILABLE: 'Available',
    OCCUPIED: 'Occupied',
    DIRTY: 'Dirty',
    CLEANING: 'Cleaning',
    READY: 'Ready',
    MAINTENANCE: 'Maintenance',
    BLOCKED: 'Blocked',
  },
  category: { LODGING: 'Lodging', SPACE: 'Space' },
  actions: {
    edit: 'Edit the resource',
    archive: 'Archive',
    archiveTitle: 'Archive “{{name}}”?',
    archiveBody:
      'The resource disappears from the calendar and availability searches. Past reservations and invoices are kept. Archiving is refused when a future reservation or block exists.',
    archiveConfirm: 'Archive the resource',
  },
  toasts: {
    created: 'Resource “{{name}}” created.',
    updated: 'Resource updated.',
    statusChanged: 'Status updated: {{status}}.',
    archived: 'Resource archived.',
    ratePlanCreated: 'Rate “{{name}}” added.',
    ratePlanUpdated: 'Rate updated.',
    ratePlanDeleted: 'Rate deleted.',
    blockCreated: 'Block created.',
    blockUpdated: 'Block updated.',
    blockDeleted: 'Block deleted.',
    feedCreated: 'iCal feed added.',
    feedSynced: 'Synchronisation complete.',
    feedDeleted: 'iCal feed removed.',
    typeRenamed: 'Resource type renamed.',
    amenitySaved: 'Amenity saved.',
  },
  drawer: {
    createTitle: 'New resource',
    editTitle: 'Edit “{{name}}”',
    subtitle: 'Inventory and public rates of the establishment',
    sectionIdentity: 'Identification',
    sectionCapacity: 'Capacity and surface',
    sectionAmenities: 'Amenities',
    sectionDuration: 'Stay duration',
    sectionNotes: 'Internal notes',
    submitCreate: 'Create the resource',
    submitEdit: 'Save changes',
    amenitiesHint:
      'Checked amenities feed the availability filters and the description printed on proformas and invoices.',
    parentHint: 'A sub-resource and its parent exclude each other (ADR-013).',
    parentNone: 'None — standalone resource',
    refHint: 'Short internal reference, for example 110 or 112-GC.',
    bedroomsHint: '0 for a studio or a meeting room.',
    durationHint: 'Minimum and maximum number of nights (or units) accepted.',
  },
  fields: {
    name: 'Resource name',
    internalRef: 'Reference',
    type: 'Type',
    floor: 'Floor',
    capacityStandard: 'Standard capacity',
    capacityMax: 'Maximum capacity',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    surface: 'Surface',
    description: 'Description',
    parent: 'Part of',
    minDuration: 'Minimum duration',
    maxDuration: 'Maximum duration',
    internalNotes: 'Internal notes',
    persons: 'guests',
    nights: 'nights',
  },
  detail: {
    back: 'Back to resources',
    tabs: {
      information: 'Information',
      rates: 'Rates',
      blocks: 'Blocks',
      composition: 'Composition',
      photos: 'Photos',
    },
    identity: 'Identification',
    features: 'Amenities',
    noAmenities: 'No amenity recorded.',
    openingHours: 'Opening hours',
    noOpeningHours: 'No time constraint.',
    notFoundTitle: 'Resource not found',
    notFoundBody: 'This resource may have been archived or belongs to another property.',
    photosSoon: 'Photo gallery coming soon',
    photosSoonBody:
      'Photo upload (with thumbnails and captions) ships with the Documents module. The descriptions entered here already appear on proformas.',
  },
  rates: {
    title: 'Rate plans',
    add: 'New rate',
    edit: 'Edit rate',
    empty: 'No rate defined',
    emptyBody: 'Add at least a default rate so the resource can be booked.',
    columns: {
      name: 'Rate',
      unit: 'Unit',
      price: 'Price',
      validity: 'Validity',
      segment: 'Segment',
      status: 'Status',
    },
    default: 'Default',
    inactive: 'Inactive',
    always: 'No limit',
    from: 'From {{date}}',
    until: 'Until {{date}}',
    deleteTitle: 'Delete rate “{{name}}”?',
    deleteBody: 'Existing reservations keep the price that was applied to them.',
    fields: {
      name: 'Rate name',
      unit: 'Pricing unit',
      price: 'Price',
      validFrom: 'Valid from',
      validTo: 'Valid until',
      minQuantity: 'Minimum quantity',
      segment: 'Customer segment',
      isDefault: 'Default rate for this resource',
      isActive: 'Active rate',
    },
    units: {
      NIGHT: 'Night',
      WEEK: 'Week',
      MONTH: 'Month',
      HOUR: 'Hour',
      HALF_DAY: 'Half day',
      DAY: 'Day',
      PACKAGE: 'Package',
    },
    perUnit: {
      NIGHT: '/ night',
      WEEK: '/ week',
      MONTH: '/ month',
      HOUR: '/ hour',
      HALF_DAY: '/ half day',
      DAY: '/ day',
      PACKAGE: '/ package',
    },
    segments: { PUBLIC: 'Public', CORPORATE: 'Corporate' },
  },
  composition: {
    title: 'Composition and inventory units',
    ruleTitle: 'Mutual exclusion rule (ADR-013)',
    ruleBody:
      'Booking a resource locks each of its inventory units for the period. A composite resource inherits the units of its sub-resources: a whole apartment and its bedrooms can therefore never be booked at the same time. Two bedrooms of the same apartment can still be rented to two different guests.',
    units: 'Locked inventory units',
    children: 'Sub-resources',
    noChildren: 'This resource has no sub-resource.',
    parentOf: 'This resource is a sub-resource of {{name}}.',
    unitCode: 'Code',
    unitName: 'Unit',
  },
  types: {
    title: 'Resource types',
    subtitle: 'Reference list used by the inventory and the rates.',
    columns: {
      code: 'Code',
      name: 'Label',
      category: 'Category',
      unit: 'Default unit',
      count: 'Resources',
    },
    rename: 'Rename',
    renameTitle: 'Rename type “{{name}}”',
    renameHint: 'The technical code ({{code}}) does not change: it is used by the API and reports.',
    system: 'System type',
  },
  amenitiesAdmin: {
    title: 'Amenity catalogue',
    subtitle: 'Checkboxes offered when creating a resource, and availability filters.',
    add: 'Add an amenity',
    addTitle: 'New amenity',
    columns: { code: 'Code', label: 'Label', usage: 'Resources', status: 'Enabled' },
    codeHint: 'Uppercase and underscores, for example POOL or GYM.',
    usage: '{{count}} resource',
    usage_other: '{{count}} resources',
    active: 'Enabled',
    inactive: 'Disabled',
  },
};

export const calendar = {
  title: 'Calendar',
  subtitle: '{{resources}} and {{children}} · {{from}} to {{to}}',
  today: 'Today',
  previous: 'Previous period',
  next: 'Next period',
  datePicker: 'Go to date',
  resourceColumn: 'Resource',
  views: { label: 'View type', day: 'Day', week: 'Week', month: 'Month', timeline: 'Timeline' },
  ranges: { label: 'Density', 7: '7 days', 14: '14 days', 30: '30 days' },
  monthSoonTitle: 'Monthly view coming soon',
  monthSoonBody:
    'The monthly grid (occupancy per day, arrivals and departures) ships with the Reservations module. Use the 30-day timeline to cover a full month.',
  monthUseTimeline: 'Show 30 days as a timeline',
  filters: { type: 'Resource type', allTypes: 'All resources' },
  expandGroup: 'Show sub-resources of {{name}}',
  collapseGroup: 'Hide sub-resources of {{name}}',
  legend: {
    label: 'Legend',
    confirmed: 'Confirmed',
    checkedin: 'In house',
    option: 'Option',
    pending: 'To confirm',
    maintenance: 'Maintenance',
    blocked: 'Block',
    unavailable: 'Unavailable — linked apartment / bedroom',
    hint: 'Select a bar to see details and actions.',
  },
  unavailable: {
    label: 'Unavailable',
    parentBooked: 'Whole apartment booked',
    childBooked: 'Bedroom booked separately',
    origin: 'Because of {{name}}',
  },
  block: { label: 'Block', maintenance: 'Maintenance' },
  month: { caption: 'Monthly occupancy, one cell per resource and day' },
  popover: {
    period: 'Period',
    resource: 'Resource',
    reason: 'Reason',
    kind: 'Kind',
    status: 'Status',
    origin: 'Origin',
    createBlock: 'Create a block',
    openResource: 'Open the resource',
    openReservation: 'Open the reservation',
    checkIn: 'Check in',
    confirm: 'Confirm the reservation',
    close: 'Close details',
  },
  selection: {
    title: 'What do you want to create?',
    description: '{{resource}} · {{period}}',
    newReservation: 'New reservation',
    newBlock: 'New block',
    cancel: 'Cancel the selection',
    laneLabel: 'Select a period on {{name}}',
  },
  emptyTitle: 'No resource to display',
  emptyBody: 'Create a resource or change the type filter to feed the planning.',
  mobile: { dayLabel: 'Day of {{date}}', free: 'Free', events: 'Events' },
  barLabel: '{{label}} — {{resource}} — {{from}} to {{to}}',
};

export const availability = {
  title: 'Availability',
  subtitle:
    'Check in one search which resources are free over a period, including bedrooms linked to an apartment.',
  search: 'Search',
  searching: 'Searching…',
  form: {
    title: 'Search criteria',
    from: 'Arrival',
    to: 'Departure',
    fromTime: 'Arrival time',
    toTime: 'Departure time',
    type: 'Resource type',
    anyType: 'All types',
    capacity: 'Guests',
    capacityHint: 'Leave empty to ignore capacity.',
    reset: 'Reset',
  },
  results: {
    title: 'Results',
    availableCount: '{{count}} available resource',
    availableCount_other: '{{count}} available resources',
    unavailableCount: '{{count}} unavailable',
    unavailableCount_other: '{{count}} unavailable',
    available: 'Available',
    unavailable: 'Unavailable',
    nights: '{{count}} night',
    nights_other: '{{count}} nights',
    estimate: 'Estimate: {{amount}}',
    book: 'Book (soon)',
    block: 'Block',
    conflictsTitle: 'Conflicts over the period',
    conflictFrom: 'from {{from}} to {{to}}',
    conflictReservation: 'Reservation on {{resource}}',
    conflictBlock: 'Block on {{resource}}',
    conflictParent: 'Parent resource {{resource}} is occupied over the period.',
    conflictChild: 'Sub-resource {{resource}} is occupied over the period.',
    conflictSelf: '{{resource}} is already occupied over the period.',
    unit: 'Unit {{code}}',
  },
  emptyTitle: 'Start a search',
  emptyBody:
    'Pick a period, optionally a type and a capacity: free resources appear in green with their rate.',
  noResultTitle: 'No matching resource',
  noResultBody: 'Widen the period, lower the requested capacity or change the type.',
  invalidRange: 'The departure date must be after the arrival date.',
};

export const maintenance = {
  title: 'Maintenance & blocks',
  subtitle:
    'Inventory blocks (maintenance, owner use, closure) and iCal feeds imported from external platforms.',
  create: 'New block',
  columns: {
    resource: 'Resource',
    kind: 'Kind',
    period: 'Period',
    reason: 'Reason',
    createdBy: 'Created by',
    actions: 'Actions',
  },
  kinds: {
    label: 'Block kind',
    MAINTENANCE: 'Maintenance',
    ADMIN: 'Administrative',
    OWNER: 'Owner use',
    CLOSURE: 'Closure',
    EXTERNAL: 'Imported (iCal)',
  },
  filters: {
    all: 'All',
    resource: 'Resource',
    allResources: 'All resources',
    period: 'Period',
    upcoming: 'Upcoming',
    current: 'In progress',
    past: 'Past',
  },
  emptyTitle: 'No block recorded',
  emptyBody: 'Create a block to take a resource off sale during works, a closure or an owner stay.',
  emptyFilteredTitle: 'No matching block',
  emptyFilteredBody: 'Clear the filters to see every block.',
  drawer: {
    createTitle: 'New block',
    editTitle: 'Edit block',
    subtitle: 'A block takes the resource off sale for the period, just like a reservation.',
    resource: 'Resource',
    startDate: 'Start',
    startTime: 'Start time',
    endDate: 'End',
    endTime: 'End time',
    reason: 'Reason',
    reasonPlaceholder: 'E.g. Air conditioner replacement — Nikiéma Froid contractor.',
    reasonHint: 'The reason appears in the calendar and in the audit log.',
    submitCreate: 'Create the block',
    submitEdit: 'Save',
    externalReadOnly:
      'This block comes from an external iCal feed: it is refreshed at each synchronisation and cannot be edited here.',
  },
  conflict: {
    title: 'Period not available',
    body: 'A reservation or a block already occupies a unit of this resource. Pick another period or free the resource concerned.',
    detail: '{{resource}} — from {{from}} to {{to}}',
  },
  delete: {
    title: 'Delete the block?',
    body: 'The resource becomes available again over the period. The action is recorded in the audit log.',
    confirm: 'Delete the block',
  },
  ical: {
    title: 'iCal feeds',
    subtitle: 'Import Booking.com or Airbnb reservations as blocks to avoid double bookings.',
    add: 'Add a feed',
    addTitle: 'New iCal feed',
    sync: 'Sync now',
    syncing: 'Syncing…',
    columns: { resource: 'Resource', source: 'Platform', lastSync: 'Last sync', status: 'Status' },
    sources: { BOOKING_COM: 'Booking.com', AIRBNB: 'Airbnb', OTHER: 'Other platform' },
    urlLabel: 'Feed URL (.ics)',
    urlHint: 'Copy the calendar export address from the platform.',
    never: 'Never synced',
    ok: 'Synced',
    error: 'Failed',
    imported: '{{count}} block imported',
    imported_other: '{{count}} blocks imported',
    empty: 'No iCal feed configured',
    emptyBody: 'Add the export URL of a Booking.com or Airbnb calendar to sync unavailability.',
    deleteTitle: 'Delete the feed?',
    deleteBody: 'Blocks already imported are kept until their end date.',
  },
};

export const errors = {
  VALIDATION_ERROR: 'Some information is invalid. Fix the highlighted fields.',
  UNAUTHENTICATED: 'You must be signed in to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Sign in again.',
  MFA_REQUIRED: 'Two-step verification is required.',
  INVALID_CREDENTIALS: 'Incorrect email address or password.',
  ACCOUNT_LOCKED: 'Account temporarily locked. Try again in a few minutes.',
  ACCOUNT_DISABLED: 'This account is disabled.',
  CSRF_TOKEN_INVALID: 'Invalid session. Reload the page and try again.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested item could not be found.',
  CONFLICT: 'This operation conflicts with the current state of the data.',
  OPTIMISTIC_LOCK_CONFLICT:
    'This record was modified in the meantime. Reload the page to start from the latest version.',
  EMAIL_ALREADY_USED: 'This email address is already used by another account.',
  ROLE_IN_USE: 'This role is assigned to users: unassign it before deleting.',
  SYSTEM_ROLE_IMMUTABLE: 'System roles cannot be modified.',
  PASSWORD_POLICY_VIOLATION: 'This password does not meet the security policy.',
  INVITATION_INVALID: 'This invitation is invalid or has expired.',
  TOKEN_INVALID: 'This link is invalid or has expired.',
  PERMISSION_ESCALATION: 'You cannot grant a permission you do not hold yourself.',
  RESOURCE_NOT_AVAILABLE:
    'This resource is not available for the requested period: a reservation or a block already occupies one of its units.',
  RESERVATION_INVALID_TRANSITION:
    'The current reservation state does not allow this action. Reload the record.',
  DEPOSIT_REQUIRED:
    'A deposit is required to confirm this reservation (D-020). A reason is needed to override it.',
  BALANCE_DUE:
    'Checking out with an outstanding balance requires a dedicated permission and a reason (R-010).',
  PAYMENT_EXCEEDS_BALANCE: 'The amount exceeds the outstanding balance.',
  PAYMENT_ALREADY_REVERSED: 'This payment has already been voided.',
  IDEMPOTENCY_KEY_REUSED:
    'This operation was already recorded with different details. Reload the page before trying again.',
  PAYMENT_ALLOCATION_EXCEEDS_AMOUNT:
    'The sum of the allocations exceeds the payment amount. Adjust the split.',
  REFUND_EXCEEDS_PAYMENT: 'The refund exceeds the amount still available on this payment.',
  CASH_SESSION_ALREADY_OPEN: 'A cash session is already open for this property.',
  CASH_SESSION_NOT_OPEN: 'Open a cash session to record a cash payment.',
  ORDER_INVALID_TRANSITION: 'This operation is not allowed in the order’s current state.',
  ORDER_HAS_PAYMENTS: 'Payments are recorded: void or refund them before cancelling the order.',
  TABLE_OCCUPIED: 'This table already has an open order.',
  PAYMENT_METHOD_NOT_ACCEPTED: 'This payment method is not accepted at this outlet.',
  TAB_NAME_IN_USE: 'An open tab already carries this name at this outlet.',
  PRODUCT_UNAVAILABLE: 'This product is not available for sale.',
  STAY_NOT_IN_HOUSE: 'This stay is not in house: only a checked-in reservation accepts charges.',
  STAY_PROPERTY_MISMATCH: 'This stay does not belong to the outlet’s property.',
  FOLIO_CLOSED: 'The folio of this stay is closed (checked out): no more charges.',
  FOLIO_CHARGE_VOIDED: 'This charge is already voided.',
  STOCK_CODE_IN_USE: 'This code is already used in this property.',
  STOCK_LOCATION_MISMATCH:
    'The location, item or outlet does not belong to this property, or is inactive.',
  STOCK_PURCHASE_NOT_DRAFT: 'This purchase order is no longer a draft.',
  STOCK_COUNT_ALREADY_OPEN: 'A stock count is already open at this location.',
  STOCK_COUNT_NOT_OPEN: 'This stock count is no longer open.',
  TENANT_SUSPENDED: 'Your organisation’s account is suspended. Contact the HOREVIA platform.',
  PLATFORM_MFA_REQUIRED:
    'The platform console requires two-factor authentication: enable it in your profile.',
  TENANT_SLUG_IN_USE: 'This tenant identifier is already in use.',
  TENANT_INVALID_TRANSITION: 'This status change is not possible in the tenant’s current state.',
  PLAN_LIMIT_REACHED: 'Plan limit reached: upgrade your subscription to add this item.',
  SUBSCRIPTION_BILLING_INCOMPLETE:
    'An invoiced subscription requires a billing period, a price and, on trial, a trial end date.',
  SUBSCRIPTION_INVOICE_INVALID_TRANSITION:
    'This operation is not possible in the current state of the subscription invoice.',
  SUBSCRIPTION_PAYMENT_EXCEEDS_BALANCE: 'The payment exceeds the remaining balance of the invoice.',
  CASH_SESSION_CLOSED: 'This cash session is closed: it no longer accepts movements.',
  RATE_LIMITED: 'Too many attempts. Please wait a moment.',
  UPLOAD_TOO_LARGE: 'File too large. Reduce the document size.',
  UPLOAD_UNSUPPORTED_TYPE: 'Unsupported format. Use a photo (JPEG, PNG, WebP, HEIC) or a PDF.',
  INVOICE_COMPLIANCE_DOCUMENTS_MISSING:
    'Final invoice blocked: mandatory documents are missing from the file (R-001).',
  INVOICE_IMMUTABLE: 'An issued invoice is immutable: use a credit note.',
  INVOICE_ALREADY_EXISTS: 'A final invoice already exists for this reservation.',
  BILLING_DOCUMENT_NOT_EDITABLE: 'This document is no longer a draft: its content cannot change.',
  PROFORMA_NOT_ISSUED:
    'This proforma must be issued and accepted before it can be converted into an invoice.',
  CREDIT_NOTE_EXCEEDS_INVOICE: 'The credit note exceeds the net amount of the source invoice.',
  NOT_IMPLEMENTED: 'This feature is not available yet.',
  INTERNAL_ERROR: 'An internal error occurred. Try again later.',
  NETWORK_ERROR: 'The server is unreachable. Check your connection.',
  MALFORMED_RESPONSE: 'The server response does not match the API contract.',
};

export const customers = {
  create: 'New customer',
  countLabel: '{{count}} customer',
  countLabel_other: '{{count}} customers',
  archived: 'Archived',
  never: 'Never',
  searchLabel: 'Search a customer',
  searchPlaceholderPeople: 'Name, phone, email or ID number',
  searchPlaceholderCompanies: 'Legal name, tax ID or trade register',
  emptyTitle: 'No customer',
  emptyBody: 'Create a customer record to book a stay in their name.',
  emptySearchTitle: 'No customer matches',
  emptySearchBody: 'Adjust the search or show archived records.',
  kind: { PERSON: 'Individual', COMPANY: 'Company' },
  gender: { M: 'Mr', F: 'Mrs', UNSPECIFIED: 'Unspecified' },
  idDocument: {
    CNIB: 'National ID (CNIB)',
    PASSPORT: 'Passport',
    RESIDENCE_PERMIT: 'Residence permit',
    DRIVING_LICENSE: 'Driving licence',
    OTHER: 'Other document',
  },
  membershipRole: {
    EMPLOYEE: 'Employee',
    MANAGER: 'Manager',
    BILLING_CONTACT: 'Billing contact',
    OTHER: 'Other',
  },
  people: {
    title: 'Individuals',
    subtitle: 'Guests: identity, ID document (national ID or passport) and linked companies.',
  },
  companies: {
    title: 'Companies',
    subtitle: 'Billed organisations: trade register, tax ID, tax regime and withholding.',
  },
  kpi: {
    people: 'Individuals',
    companies: 'Companies',
    totalFoot: 'active records on this property',
    withPhone: 'Reachable by phone',
    withPhoneFoot: 'primary number on file',
    withEmail: 'Reachable by email',
    withEmailFoot: 'email address on file',
    linked: 'Links',
    linkedFoot: 'primary company on file',
  },
  filters: { showArchived: 'Show archived' },
  columns: {
    person: 'Individual',
    company: 'Company',
    contact: 'Contact',
    phone: 'Phone',
    email: 'Email',
    legal: 'Identification',
    stays: 'Stays',
    balance: 'Balance',
    lastStay: 'Last stay',
    role: 'Role',
    primary: 'Default payer',
  },
  fields: {
    gender: 'Title',
    lastName: 'Last name',
    firstNames: 'First names',
    birthDate: 'Date of birth',
    nationality: 'Nationality',
    profession: 'Occupation',
    idDocumentIssuingCountry: 'Issuing country',
    country: 'Country',
    idDocument: 'National ID or passport',
    idDocumentType: 'Document type',
    idDocumentNumber: 'Document number',
    idDocumentExpiresAt: 'Expiry',
    legalName: 'Legal name',
    legalForm: 'Legal form',
    taxRegime: 'Tax regime',
    taxDivision: 'Tax division',
    billingContactName: 'Billing contact',
    billingEmail: 'Billing email',
    withholdingDefault: 'Offer 5% withholding tax',
    taxExempt: 'Tax exempt',
    phonePrimary: 'Phone',
    phoneSecondary: 'Secondary phone',
    email: 'Email address',
    address: 'Address',
    poBox: 'PO box',
    city: 'City',
    notes: 'Internal notes',
    company: 'Company',
    person: 'Individual',
    membershipRole: 'Role in the company',
  },
  search: {
    change: 'Change',
    noResult: 'No result for “{{term}}”',
    create: 'Create “{{term}}”',
    createPerson: 'Create an individual',
    createCompany: 'Create the company',
    personPlaceholder: 'Name, phone, email or company',
    companyPlaceholder: 'Legal name or tax ID',
  },
  drawer: {
    createTitle: 'New customer',
    editTitle: 'Edit {{name}}',
    subtitle: 'The record can be completed later: only name and phone are required.',
    kindLegend: 'Customer type',
    sectionIdentity: 'Identity',
    sectionIdDocument: 'National ID or passport',
    idDocumentNotice:
      'The ID document (national ID or passport) and the signed police form are required before a final invoice can be issued (rule R-001).',
    sectionCompany: 'Company identification',
    sectionContact: 'Contact details',
    sectionEmployer: 'Company link',
    rccmHint: 'Printed on every invoice.',
    ifuHint: 'Unique tax identifier.',
    withholdingHint:
      '5% withholding computed on the discounted amount, enabled document by document (D-012).',
    employerHint: 'The invoice can be issued to the company; the link can be changed at any time.',
    phoneHint: 'International format, e.g. +226 70 24 18 55.',
    phoneInvalid: 'Invalid phone number for Burkina Faso.',
    submitCreate: 'Create customer',
    submitEdit: 'Save changes',
  },
  actions: {
    newReservation: 'New reservation',
    archive: 'Archive record',
    restore: 'Restore record',
    archiveTitle: 'Archive {{name}}?',
    archiveBody:
      'The record stays readable and history is kept, but it no longer appears in lists or search.',
    restoreTitle: 'Restore {{name}}?',
    restoreBody: 'The record becomes selectable again in reservations and search.',
  },
  detail: {
    tabsLabel: 'Customer record sections',
    tabs: {
      stays: 'Stays',
      billing: 'Billing',
      companies: 'Linked companies',
      employees: 'Employees',
      documents: 'Documents',
      notes: 'Notes',
      loyalty: 'Loyalty',
      interactions: 'Contacts',
    },
    staysTitle: 'Customer stays',
    staysEmptyTitle: 'No stay',
    staysEmptyBody: 'This customer has no reservation yet.',
    staysCount: 'Stays',
    balance: 'Outstanding balance',
    lastStay: 'Last stay',
    billedTitle: 'Stays billed to this customer',
    billedEmptyTitle: 'Nothing billed',
    billedEmptyBody: 'No stay is billed to this record yet.',
    invoicesSoon: 'Proformas and final invoices arrive in the billing sprint.',
    linkCompany: 'Link a company',
    linkPerson: 'Link an individual',
    linkBody: 'The link records the person’s role within the company.',
    linkSubmit: 'Create link',
    linksEmptyTitle: 'No link',
    linksEmptyBody: 'Link this record to a company to prefill “Billed to”.',
    primaryBadge: 'Default payer',
    unlink: 'Remove link',
    documentsSoonTitle: 'Customer documents',
    documentsSoonBody: 'Uploading ID documents and police forms arrives in sprint 2.4.',
    notesEmpty: 'No internal note for this customer.',
    complianceTitle: 'Missing ID document',
    complianceBody:
      'Without a recorded national ID or passport, the final invoice will be refused by the server (R-001).',
    idMissing: 'Not provided',
  },
  toasts: {
    created: '{{name}} created.',
    updated: 'Customer record updated.',
    archived: 'Record archived.',
    restored: 'Record restored.',
    linked: 'Link created.',
    unlinked: 'Link removed.',
  },
};

export const reservations = {
  title: 'Reservations',
  subtitle: 'Every reservation on the property: requests, options, ongoing stays and history.',
  listTitle: 'Reservation list',
  countLabel: '{{count}} reservation',
  countLabel_other: '{{count}} reservations',
  searchLabel: 'Search a reservation',
  searchPlaceholder: 'Reference, guest, company or resource',
  emptyTitle: 'No reservation',
  emptyBody: 'Create a first reservation from the calendar or the button above.',
  emptySearchTitle: 'No reservation matches',
  emptySearchBody: 'Adjust the search or clear the filters.',
  nightsCount: '{{count}} night',
  nightsCount_other: '{{count}} nights',
  adultsCount: '{{count}} adult',
  adultsCount_other: '{{count}} adults',
  childrenCount: '{{count}} child',
  childrenCount_other: '{{count}} children',
  childrenNone: 'None',
  status: {
    DRAFT: 'Draft',
    PENDING_CONFIRMATION: 'Pending',
    OPTION: 'Option',
    CONFIRMED: 'Confirmed',
    CHECKED_IN: 'In house',
    CHECKED_OUT: 'Completed',
    CANCELLED: 'Cancelled',
    NO_SHOW: 'No-show',
  },
  compliance: {
    NOT_REQUIRED: 'Not required',
    INCOMPLETE: 'Compliance incomplete',
    COMPLETE: 'Compliance complete',
  },
  complianceItems: {
    IDENTITY_DOCUMENT: 'National ID or passport',
    POLICE_FORM: 'Signed police form',
  },
  source: {
    DIRECT: 'Direct',
    PHONE: 'Phone',
    WHATSAPP: 'WhatsApp',
    EMAIL: 'Email',
    WEBSITE: 'Website',
    BOOKING_COM: 'Booking.com',
    AIRBNB: 'Airbnb',
    CORPORATE: 'Corporate',
    REFERRAL: 'Referral',
    OTHER: 'Other',
  },
  kpi: {
    active: 'Active reservations',
    activeFoot: 'options, confirmed and ongoing stays',
    options: 'Open options',
    optionsFoot: 'inventory held until expiry',
    balance: 'Balance to collect',
    balanceFoot: 'across the listed reservations',
    compliance: 'Incomplete files',
    complianceFoot: 'final invoice blocked (R-001)',
  },
  filters: {
    resource: 'Resource',
    allResources: 'All resources',
    from: 'Arrival from',
    to: 'Arrival until',
    balanceDue: 'Balance due',
  },
  columns: {
    reference: 'Reference',
    guest: 'Guest',
    resource: 'Resource',
    dates: 'Dates',
    nights: 'Nights',
    total: 'Net payable',
    balance: 'Balance',
    status: 'Status',
    compliance: 'Compliance',
  },
  list: { billedTo: 'Billed to {{name}}' },
  fields: {
    arrivalDate: 'Arrival date',
    arrivalTime: 'Arrival time',
    departureDate: 'Departure date',
    departureTime: 'Departure time',
    arrival: 'Arrival',
    departure: 'Departure',
    nights: 'Nights',
    adults: 'Guests — adults',
    children: 'Guests — children',
    occupants: 'Guests',
    earlyCheckin: 'Early check-in requested',
    guest: 'Guest — search an existing customer',
    billingParty: 'Billed to',
    billingCompany: 'Billed company',
    ratePlan: 'Rate plan',
    unitPrice: 'Unit price',
    quantity: 'Billed quantity',
    discount: 'Commercial discount',
    discountMode: 'Discount type',
    extras: 'Extras',
    extraQuantity: 'Quantity — {{label}}',
    extraPrice: 'Unit price — {{label}}',
    applyWithholding: 'Apply the {{rate}} withholding tax',
    notesInternal: 'Internal notes',
    optionExpiresAt: 'Option expiry',
    resource: 'Resource',
    property: 'Property',
    source: 'Channel',
    createdAt: 'Created on',
  },
  extras: {
    BREAKFAST: 'Breakfast',
    AIRPORT_SHUTTLE: 'Airport shuttle',
    EXTRA_CLEANING: 'Extra cleaning',
    LAUNDRY: 'Laundry',
  },
  create: {
    action: 'New reservation',
    title: 'New reservation',
    subtitle: 'Three steps on a single page: the stay, the customer, the price.',
    breadcrumb: 'New',
    step1: 'Stay',
    step2: 'Customer',
    step3: 'Price & extras',
    checkinHint: 'Check-in from 12:00 (unless expressly requested).',
    checkoutHint: 'Check-out by 12:00 at the latest.',
    datesInvalid: 'Departure must be after arrival.',
    earlyCheckinHint: 'Flag it to the front desk: arriving before 12:00 depends on availability.',
    capacityExceeded: '{{name}} accommodates {{max}} people at most.',
    resourceLegend: 'Suggested resource',
    availabilityCount: '{{count}} resource available for these dates for {{people}} people.',
    availabilityCount_other: '{{count}} resources available for these dates for {{people}} people.',
    compositeNotice:
      'Booking a whole apartment makes its rooms unavailable; booking a single room makes the whole apartment unavailable. The rule is enforced by the server.',
    capacityLabel: '{{count}} person max',
    capacityLabel_other: '{{count}} people max',
    subResource: 'part of an apartment',
    subResourceBadge: 'Sub-resource',
    available: 'Available',
    unavailable: 'Unavailable',
    conflictWith: 'Occupied by {{name}}',
    guestHint: 'Search runs instantly from 2 characters.',
    billingHint:
      'The billed party may differ from the guest — typical of stays covered by an employer.',
    billingGuest: 'The guest',
    billingCompany: 'A company',
    billingGuestNotice: 'The invoice will be issued to {{name}}, at their personal address.',
    billingGuestEmpty: 'Select the guest first.',
    noRatePlan: 'No rate plan',
    priceOverrideHint: 'Editable: every override is written to the audit log.',
    priceLockedHint: 'Read-only — permission “reservation.override_price” required.',
    discountHint: 'That is {{amount}} off the accommodation.',
    removeExtra: 'Remove extra {{label}}',
    withholdingHint:
      'CME regime: no VAT charged. The 5% withholding is computed on the discounted amount.',
    notesPlaceholder: 'Visible to the team only.',
    confirmAction: 'Create and confirm',
    holdAction: 'Hold an option',
    draftAction: 'Save as draft',
    holdTitle: 'Hold an option',
    holdHint: 'Suggested expiry: +48 h. Inventory is held until then.',
    holdSubmit: 'Create the option',
    depositTitle: 'Confirmation deposit',
    depositSubtitle: 'Free amount: any recorded deposit allows moving to Confirmed (D-022).',
    depositSubmit: 'Collect and confirm',
    depositNoPermission: 'You are not allowed to record a payment.',
    withoutDepositAction: 'Confirm without deposit',
    withoutDepositTitle: 'Confirm without deposit?',
    withoutDepositWarning:
      'The full balance of {{amount}} remains due on arrival. No payment is recorded by this action.',
    withoutDepositReason: 'Reason for confirming without deposit',
    withoutDepositReasonHint: 'Required (10 characters min). The reason is kept in the audit log.',
    withoutDepositSubmit: 'Confirm without deposit',
    incomplete: 'Complete the stay and the customer before saving.',
  },
  summary: {
    title: 'Summary',
    noResource: 'No resource selected',
    customer: 'Customer',
    noGuest: 'No guest selected',
    billedTo: 'Billed to {{name}}',
    amounts: 'Amounts',
    accommodation: 'Accommodation {{quantity}} × {{price}}',
    subtotal: 'Subtotal',
    discount: 'Discount',
    tax: 'VAT (CME regime)',
    withholding: '5% withholding tax',
    net: 'Net payable',
    paid: 'Paid',
    balance: 'Outstanding balance',
    depositTitle: 'Deposit required to confirm (free amount)',
    depositHint:
      'No percentage or minimum imposed: any recorded deposit allows moving to Confirmed.',
    quotePending: 'Pick a resource and dates to get the quote.',
    complianceNotice:
      'National ID or passport and a signed police form will be required at check-in before any final invoice.',
  },
  deposit: {
    suggestBalance: 'Balance',
    suggestHalf: '50%',
    amount: 'Deposit amount',
    amountHint: 'Free amount, greater than 0 and at most the outstanding balance (D-022).',
    amountRequired: 'Enter an amount greater than 0.',
    amountTooHigh: 'The amount exceeds the outstanding balance.',
    method: 'Payment method',
    provider: 'Provider',
    reference: 'Transaction reference',
    referenceHint: 'Required for every method except cash.',
    referenceRequired: 'Enter the transaction reference.',
    note: 'Note',
  },
  depositMethod: {
    CASH: 'Cash',
    MOBILE_MONEY: 'Mobile Money',
    BANK_TRANSFER: 'Bank transfer',
    CHEQUE: 'Cheque',
    CARD: 'Card',
    OTHER: 'Other',
  },
  depositProvider: {
    ORANGE_MONEY: 'Orange Money',
    MOOV_MONEY: 'Moov Money',
    WAVE: 'Wave',
    CORIS_MONEY: 'Coris Money',
    CORIS_BANK: 'Coris Bank',
    ECOBANK: 'Ecobank',
    UBA: 'UBA',
    OTHER: 'Other',
  },
  cancelReasons: {
    CUSTOMER_REQUEST: 'Cancelled at the customer’s request',
    NO_SHOW_ANTICIPATED: 'Anticipated no-show',
    INPUT_ERROR: 'Data entry error',
    UNAVAILABLE: 'Resource unavailable',
  },
  actions: {
    hold: 'Hold an option',
    holdTitle: 'Hold an option on {{reference}}',
    holdBody: 'Inventory is held until the option expires.',
    holdHint: 'Suggested expiry: +48 h.',
    confirm: 'Confirm',
    confirmTitle: 'Confirm {{reference}}?',
    confirmBody: 'Confirming holds the inventory for the whole period.',
    confirmDepositOk: 'Deposit of {{amount}} already collected: confirmation is possible.',
    confirmNoDepositWarning:
      'No deposit collected: the {{amount}} balance stays due. A reason is required.',
    confirmDepositRequired:
      'A deposit is required to confirm. Record a payment or ask the manager to confirm without one.',
    checkIn: 'Check-in',
    checkInTitle: 'Check-in — {{reference}}',
    checkInBody: 'Arrival time, guests present and key handover.',
    checkInComplianceTitle: 'Compliance documents incomplete',
    checkInComplianceBody:
      'Check-in is still allowed, but the final invoice will be blocked while documents are missing (R-001).',
    checkInTime: 'Actual arrival time',
    occupants: 'Guests (one per line)',
    occupantsHint: 'The first name is the main guest.',
    keysHanded: 'Keys handed over',
    checkOut: 'Check-out',
    checkOutTitle: 'Check-out — {{reference}}',
    checkOutBody: 'Departure time and end-of-stay notes.',
    checkOutTime: 'Actual departure time',
    checkOutBalanceTitle: 'Outstanding balance: {{amount}}',
    checkOutBalanceBlocked:
      'Check-out with an outstanding balance requires the dedicated permission (R-010). Collect the balance or ask the manager.',
    checkOutBalanceAllowed:
      'Check-out with a balance is possible with a mandatory reason, recorded in the audit log (D-014).',
    checkOutReason: 'Reason for checking out with a balance',
    checkOutReasonHint: 'Required, 10 characters minimum.',
    checkOutReasonTooShort: 'The reason must be at least 10 characters long.',
    checkOutAccommodation: 'Accommodation and extras',
    checkOutFolio_one: 'Folio ({{count}} charge)',
    checkOutFolio_other: 'Folio ({{count}} charges)',
    checkOutConsolidated: 'Consolidated balance',
    departureNotes: 'Departure notes',
    checkOutPageTitle: 'Check-out — {{reference}}',
    checkOutResourceState: 'Unit state on departure',
    checkOutResourceHint:
      'The unit automatically becomes “Dirty”: it will show up in Operations › Housekeeping.',
    checkOutDirtyAfter: 'After validation, {{name}} will be marked “Dirty”.',
    checkOutComplianceWarning:
      'Compliance incomplete: check-out is still allowed, but the final invoice stays blocked (R-001).',
    checkOutBalanceRecordDeposit: 'Record a deposit',
    checkOutPage: 'Open the check-in page',
    cancel: 'Cancel reservation',
    cancelTitle: 'Cancel {{reference}}?',
    cancelBody:
      'The inventory of {{name}} will be released. The action is logged and irreversible.',
    cancelConfirm: 'Confirm cancellation',
    cancelReason: 'Cancellation reason',
    cancelDetails: 'Details',
    cancelFee: 'Cancellation fee',
    cancelFeeHint: 'Policy: free cancellation up to 48 h before arrival.',
    cancelDepositNotice:
      'The {{amount}} deposit already collected will need a refund or a credit note.',
    noShow: 'Report a no-show',
    noShowTitle: 'Report a no-show — {{reference}}',
    noShowBody: 'Inventory is released; the reservation cannot be reactivated.',
    noShowFee: 'No-show fee',
    noShowFeeHint: 'Policy: one night by default.',
    recordDeposit: 'Record a deposit',
    recordDepositTitle: 'Record a deposit',
    sendConfirmation: 'Send confirmation',
    sendTitle: 'Send confirmation',
    sendBody: 'A stay confirmation email is sent to the customer.',
    sendEmail: 'Send by email',
    sendWhatsappSoon: 'WhatsApp — coming soon',
    sendDocument: 'Open the confirmation (PDF)',
  },
  checkIn: {
    title: 'Check-in — {{guest}}',
    subtitle:
      '{{resource}} · expected on {{date}} from {{time}} · {{nights}} night(s) · {{adults}} adult(s)',
    breadcrumb: 'Check-in',
    backToReservation: 'Back to reservation',
    complianceBannerTitle: 'Compliance INCOMPLETE — check-in is still allowed',
    complianceBannerBody:
      '{{count}} mandatory document still has to be validated. You may welcome the guest right away, but the server will refuse to issue the final invoice until every mandatory document is validated — uploading alone is not enough (code',
    complianceBannerBody_other:
      '{{count}} mandatory documents still have to be validated. You may welcome the guest right away, but the server will refuse to issue the final invoice until every mandatory document is validated — uploading alone is not enough (code',
    documentsTitle: 'Mandatory documents',
    occupantsTitle: 'Guests on site',
    occupantsHint: '{{announced}} guest(s) announced · maximum capacity: {{capacity}}',
    addOccupant: 'Add',
    mainOccupant: 'Main guest',
    occupantNumber: 'Guest {{index}}',
    occupantPlaceholder: 'Full name',
    removeOccupant: 'Remove this guest',
    arrivalTitle: 'Arrival & keys',
    auditedHint: 'Recorded in the audit log',
    actualArrival: 'Actual arrival time',
    actualArrivalHint: 'Check-in from 12:00. Before that, tick “early check-in requested”.',
    agent: 'Front desk agent',
    earlyCheckin: 'Early check-in requested (before 12:00)',
    keysHanded: 'Keys handed over to the guest',
    notes: 'Notes',
    notesPlaceholder: 'Remarks on the inventory of fixtures, guest requests…',
    acknowledgeTitle: 'Documents not validated',
    acknowledgeBody:
      'Check-in is possible, but the final invoice stays blocked until these documents are uploaded and validated. Your confirmation is logged.',
    acknowledgeLabel: 'I confirm the check-in with missing documents',
    submit: 'Complete check-in',
    progressTitle: 'Check-in progress',
    stepDone: 'done',
    stepTodo: 'to do',
    steps: {
      confirmed: 'Reservation confirmed',
      documents: 'Mandatory documents validated',
      resource: 'Unit ready',
      keys: 'Keys available and handed over',
    },
    billingEffectTitle: 'Impact on billing',
    billingEffectStay: 'Check-in and stay allowed',
    billingEffectProforma: 'Proforma and payments allowed',
    billingEffectInvoice: 'Final invoice refused by the server',
  },
  edit: {
    title: 'Edit {{reference}}',
    subtitle: 'Dates, resource, guests and price. Availability is re-checked.',
    resourceHint: 'Resources occupied during the period are disabled.',
  },
  detail: {
    subtitle:
      '{{guest}} · {{resource}} · {{from}} → {{to}} · {{nights}} night(s) · billed to {{billedTo}}',
    tabsLabel: 'Reservation sections',
    tabs: {
      stay: 'Stay',
      customer: 'Customer & billing',
      documents: 'Documents & compliance',
      payments: 'Payments',
      folio: 'Folio',
      history: 'History',
    },
    billingSubject: 'Rental of {{resource}} from {{from}} to {{to}}',
    netDetail: 'Subtotal {{subtotal}}',
    folioDetail: 'incl. folio {{amount}}',
    depositsCount: '{{count}} deposit recorded',
    depositsCount_other: '{{count}} deposits recorded',
    balanceDue: 'Due on arrival',
    stayTitle: 'Stay details',
    earlyCheckin: 'Early check-in requested',
    linesTitle: 'Rate lines',
    lineLabel: 'Description',
    lineUnitPrice: 'Unit',
    lineQuantity: 'Qty',
    lineAmount: 'Amount',
    notesTitle: 'Internal notes',
    guestTitle: 'Guest',
    openCustomer: 'Open customer record',
    billingTitle: 'Billed to',
    openBillingParty: 'Open record',
    withholdingApplied: '5% withholding applied on the discounted amount (D-012).',
    complianceIncompleteTitle: 'Compliance incomplete — {{count}} required document to validate',
    complianceIncompleteTitle_other:
      'Compliance incomplete — {{count}} required documents to validate',
    complianceIncompleteBody:
      'Check-in remains allowed. However, issuing the final invoice will be refused by the server until every mandatory document is validated — uploading alone is not enough (rule R-001).',
    complianceMissingTypes: 'Expected documents: {{types}}.',
    complianceCompleteTitle: 'Compliance complete',
    complianceCompleteBody: 'The final invoice can be issued.',
    complianceNotRequired: 'No compliance document required for this resource.',
    requirementsTitle: 'Document requirements',
    requirementsCount: '{{validated}} of {{total}} validated',
    documentProvided: 'Document recorded in the file.',
    documentMissing: 'To record before issuing the final invoice.',
    provided: 'Provided',
    missing: 'Missing',
    uploadSoonTitle: 'Upload coming soon',
    uploadSoonBody: 'Dropping documents (PDF, JPEG, PNG) arrives in sprint 2.4.',
    paymentsTitle: 'Deposits collected',
    paymentsEmptyTitle: 'No deposit',
    paymentsEmptyBody: 'Record a deposit to allow confirming the reservation.',
    paymentReference: 'Reference',
    paymentDate: 'Date',
    paymentMethod: 'Method',
    paymentAmount: 'Amount',
    paymentsFooter: 'Deposits will be allocated to proformas and invoices in the billing sprint.',
    historyTitle: 'Reservation log',
    appendOnly: 'Append-only',
    historyEmptyTitle: 'No event',
    actionsTitle: 'Actions',
    optionTitle: 'Option in progress',
    optionBody: 'Expires on {{date}} — after that, inventory is released automatically.',
    complianceCardTitle: 'Compliance',
    requiredDocuments: 'Required documents',
    invoiceBlocked: 'Final invoice blocked while the file is incomplete.',
  },
  folio: {
    title: 'Stay folio',
    notInHouse: 'Charges are posted on an in-house stay (after check-in).',
    closedTitle: 'Folio closed',
    closedBody: 'Checked out on {{date}}: no more charges or reversals.',
    addCharge: 'Manual charge',
    addChargeTitle: 'Post a charge on the stay',
    addChargeBody: 'Minibar, laundry, damage… The charge is added to the stay balance immediately.',
    post: 'Post {{amount}}',
    emptyTitle: 'No charge',
    emptyBody: 'Bills charged from the restaurant or bar and front-desk charges will appear here.',
    columns: {
      postedAt: 'Posted on',
      label: 'Label',
      source: 'Source',
      signature: 'Signed slip',
      amount: 'Amount',
    },
    kind: { POS_ORDER: 'Outlet', MANUAL: 'Front desk' },
    signed: 'Signed',
    unsigned: 'Not attached',
    attachSignature: 'Attach signed slip',
    signTitle: 'Signed transfer slip — {{reference}}',
    signBody:
      'Scan or photograph the slip signed by the guest: it is attached to the stay and to the charge.',
    void: 'Void charge',
    voidTitle: 'Void “{{label}}”',
    voidBody: 'The charge stays visible with its reason; a charged bill reopens at the outlet.',
    voidedBy: 'Voided by {{name}} — {{reason}}',
    footer_one: '{{count}} active charge',
    footer_other: '{{count}} active charges',
    fields: {
      label: 'Label',
      labelPlaceholder: 'Minibar, laundry…',
      description: 'Details',
      quantity: 'Quantity',
      unitPrice: 'Unit price',
    },
    toasts: {
      posted: 'Charge posted on the stay.',
      voided: 'Charge voided.',
      signed: 'Signed slip attached to the charge.',
    },
  },
  historyActions: {
    reservation_create: 'Reservation created',
    reservation_update: 'Reservation updated',
    reservation_hold: 'Option held',
    reservation_confirm: 'Reservation confirmed',
    reservation_check_in: 'Checked in',
    reservation_check_out: 'Checked out',
    reservation_cancel: 'Reservation cancelled',
    reservation_no_show: 'No-show reported',
    reservation_option_expired: 'Option expired',
    reservation_deposit_recorded: 'Deposit recorded',
    reservation_confirmation_sent: 'Confirmation sent',
  },
  errors: {
    notAvailableTitle: 'Period unavailable',
    notAvailableBody:
      'A reservation or a block already occupies one of this resource’s units during the requested period.',
    depositRequiredTitle: 'Deposit required',
    depositRequiredBody:
      'A deposit is required to confirm the reservation (D-020). A manager or owner can confirm without one, with a reason.',
    paymentExceedsTitle: 'Amount above the balance',
    paymentExceedsBody:
      'A deposit cannot exceed the outstanding balance ({{balance}}): no unallocated overpayment at MVP (R-008).',
    balanceDueTitle: 'Outstanding balance',
    balanceDueBody:
      'Checking out with a balance requires the dedicated permission and a mandatory reason (R-010).',
    invalidTransitionTitle: 'Transition not allowed',
    invalidTransitionBody:
      'The current reservation state ({{status}}) does not allow this action. Reload the record: a colleague may have changed it.',
  },
  toasts: {
    created: 'Reservation {{reference}} created.',
    updated: 'Reservation updated.',
    hold: 'Option held.',
    confirm: 'Reservation confirmed.',
    checkIn: 'Check-in recorded.',
    checkOut: 'Check-out recorded.',
    cancel: 'Reservation cancelled.',
    noShow: 'No-show reported.',
    deposit: 'Deposit recorded.',
    send: 'Confirmation sent.',
  },
};

export const documents = {
  title: 'Documents',
  unknownType: 'Unknown type',
  type: {
    IDENTITY_DOCUMENT: 'ID card or passport',
    POLICE_FORM: 'Completed police form',
    CONTRACT: 'Contract',
    PURCHASE_ORDER: 'Purchase order',
    PAYMENT_RECEIPT: 'Payment receipt',
    CONFIRMATION_PDF: 'Stay confirmation (PDF)',
    PROFORMA_PDF: 'Proforma (PDF)',
    INVOICE_PDF: 'Invoice (PDF)',
    PHOTO: 'Photo',
    SIGNED_SLIP: 'Signed transfer slip',
    OTHER: 'Other document',
  },
  identityKind: {
    CNIB: 'National ID (CNIB)',
    PASSPORT: 'Passport',
    RESIDENCE_PERMIT: 'Residence permit',
    OTHER: 'Other ID document',
  },
  status: {
    PENDING_UPLOAD: 'Awaiting upload',
    PENDING_SCAN: 'Virus scan in progress',
    CLEAN: 'Checked',
    REJECTED: 'Rejected',
    DELETED: 'Deleted',
  },
  fields: {
    type: 'Document type',
    identityKind: 'ID document kind',
    identityKindHint: 'National ID for residents, passport for foreign travellers.',
    label: 'Label (optional)',
    labelHint: 'E.g. “ID no. B0245789” or “ABC Burkina purchase order 2026-0451”.',
  },
  upload: {
    dropTitle: 'Drop a document or click to browse',
    dropTitleFor: 'Drop the “{{type}}” document',
    dropHint: 'PDF, JPEG, PNG, WebP or HEIC — {{max}} MB maximum · private storage, temporary URLs',
    browse: 'Choose a file',
    camera: 'Take a photo',
    submit: 'Upload document',
    remove: 'Remove selected file',
    previewAlt: 'Preview of {{name}}',
    progressLabel: 'Upload progress',
    progressValue: 'Uploading: {{percent}}%',
  },
  actions: {
    add: 'Add a document',
    preview: 'View',
    previewOf: 'Preview {{name}}',
    download: 'Download',
    downloadOf: 'Download {{name}}',
    deleteOf: 'Delete {{name}}',
  },
  list: {
    meta: '{{filename}} · {{size}} · uploaded on {{date}} by {{author}}',
    unknownAuthor: 'a colleague',
    restricted: 'Restricted access',
  },
  viewer: {
    title: 'Document preview',
    subtitle: '{{type}} · {{size}} · {{date}}',
    sensitiveNotice:
      'ID document: every access is logged by name and the URL expires within minutes.',
    openNewTab: 'Open in a new tab',
  },
  delete: {
    title: 'Delete this document?',
    warning:
      'The deletion is recorded in the audit log. A compliance requirement satisfied by this document becomes missing again.',
    reason: 'Deletion reason',
    reasonHint: 'Required, at least 5 characters.',
    reasonTooShort: 'The reason must be at least 5 characters long.',
    validatedForCompliance:
      'This document backs an already validated compliance requirement: reject the requirement first in “Documents & compliance”, then delete the document (R-001).',
  },
  accessLog: {
    title: 'Access history',
    subtitle: 'Views and downloads of this document (named log).',
    empty: 'No access recorded yet.',
    action: { VIEW: 'View', DOWNLOAD: 'Download', UPLOAD: 'Upload', DELETE: 'Deletion' },
    columns: { action: 'Action', user: 'User', date: 'Date', ip: 'IP address' },
  },
  others: {
    title: 'Other documents',
    subtitle: 'Contract, purchase order, receipt, photo…',
    emptyTitle: 'No other document',
    emptyBody: 'Add a contract, a purchase order or a confirmation screenshot.',
  },
  customer: {
    title: 'Customer documents',
    subtitle:
      'An ID document attached to the customer can be reused for their next stays while it is valid.',
    emptyTitle: 'No document',
    emptyBody: 'Add the customer ID or passport to speed up their next check-ins.',
  },
  errors: {
    uploadFailedTitle: 'Upload failed',
    tooLarge: 'File too large ({{max}} MB maximum). On mobile, reduce the photo size.',
    unsupportedType: 'Unsupported format. Use a photo (JPEG, PNG, WebP, HEIC) or a PDF.',
    forbiddenTitle: 'Access denied',
    forbiddenBody:
      'Opening ID documents requires the “document.view_identity” permission. Ask your manager.',
  },
  toasts: { uploaded: 'Document added.', deleted: 'Document deleted.' },
};

export const compliance = {
  title: 'Compliance',
  optional: 'optional',
  gauge: '{{validated}} of {{total}}',
  gaugeLabel: 'Mandatory documents validated',
  gaugeAwaiting: '{{count}} document uploaded, awaiting validation.',
  gaugeAwaiting_other: '{{count}} documents uploaded, awaiting validation.',
  status: {
    MISSING: 'Missing',
    UPLOADED: 'To review',
    VALIDATED: 'Validated',
    REJECTED: 'Rejected',
  },
  hints: {
    missing: 'Required before the final invoice can be issued.',
    uploaded:
      'Uploaded on {{date}} by {{author}} — awaiting validation: the file stays incomplete.',
    validated: 'Validated on {{date}} by {{author}}.',
    rejected: 'Rejected: {{reason}}',
  },
  actions: {
    validate: 'Validate',
    reject: 'Reject',
    addFor: 'Add {{type}}',
    replace: 'Replace',
  },
  reject: {
    title: 'Reject the document',
    reason: 'Rejection reason',
    reasonHint: 'Required, at least 5 characters. The reason is visible to the team.',
  },
  requirements: {
    title: 'Document requirements',
    subtitle:
      'Documents required per resource type. An unmet mandatory requirement blocks the final invoice (R-001).',
    columns: {
      resourceType: 'Resource type',
      document: 'Document',
      mandatory: 'Mandatory',
      appliesTo: 'Applies to',
      reminder: 'Reminder',
      actions: 'Actions',
    },
    appliesTo: { MAIN_GUEST: 'Main guest', ALL_OCCUPANTS: 'All occupants' },
    mandatoryYes: 'Mandatory',
    mandatoryNo: 'Optional',
    reminderNone: 'None',
    reminderHours: 'After {{count}} h',
    add: 'Add a requirement',
    addTitle: 'New document requirement',
    save: 'Save requirement',
    delete: 'Delete requirement',
    deleteConfirm: 'Delete this requirement? Ongoing stays will be recomputed.',
    emptyTitle: 'No requirement configured',
    emptyBody: 'Add the ID document and the police form for lodging resource types.',
    noPermission: 'Configuring requirements needs the “compliance.manage_requirements” permission.',
    reminderLabel: 'Reminder after (hours)',
    reminderPlaceholder: 'E.g. 24',
  },
  toasts: {
    validated: 'Document validated: compliance updated.',
    rejected: 'Document rejected: the requirement is missing again.',
    requirementSaved: 'Requirement saved.',
    requirementSavedResync: 'Requirement saved · {{count}} ongoing stay recomputed.',
    requirementSavedResync_other: 'Requirement saved · {{count}} ongoing stays recomputed.',
    requirementDeleted: 'Requirement deleted.',
    requirementDeletedItems: 'Requirement deleted · {{count}} pending document removed.',
    requirementDeletedItems_other: 'Requirement deleted · {{count}} pending documents removed.',
  },
};

export const housekeeping = {
  title: 'Housekeeping',
  subtitle: 'Units to prepare — {{date}}',
  countLabel: '{{count}} resource',
  countLabel_other: '{{count}} resources',
  status: {
    AVAILABLE: 'Available',
    OCCUPIED: 'Occupied',
    DIRTY: 'Dirty',
    CLEANING: 'Cleaning',
    READY: 'Ready',
    MAINTENANCE: 'Maintenance',
    BLOCKED: 'Blocked',
  },
  filters: { label: 'Filter by status', all: 'All statuses' },
  card: {
    currentGuest: 'Guest: {{name}}',
    vacant: 'Vacant unit',
    departure: 'Departure {{date}}',
    nextArrival: 'Next arrival {{date}}',
    nextGuest: 'Next guest: {{name}}',
    noNextArrival: 'No arrival scheduled',
    since: 'Status unchanged since {{date}}',
  },
  actions: {
    start: 'Start cleaning',
    ready: 'Mark as ready',
    noPermission: 'Changing the status needs the “housekeeping.update_status” permission.',
  },
  legend: 'Status legend',
  emptyTitle: 'Nothing to prepare',
  emptyBody: 'Every unit is ready or occupied. Good work.',
  toasts: {
    CLEANING: 'Cleaning started: {{name}}.',
    READY: '{{name}} is ready.',
    DIRTY: '{{name}} is dirty again.',
  },
};

export const tasks = {
  title: 'Tasks',
  subtitle: 'Files to complete, expected payments and expiring options.',
  countLabel: '{{count}} open task',
  countLabel_other: '{{count}} open tasks',
  kind: {
    DOCUMENT_MISSING: 'Missing document',
    PAYMENT_EXPECTED: 'Expected payment',
    RESERVATION_TO_CONFIRM: 'Reservation to confirm',
    OPTION_EXPIRING: 'Expiring option',
    ARRIVAL_TODAY: 'Arrival today',
    DEPARTURE_TODAY: 'Departure today',
    HOUSEKEEPING: 'Housekeeping',
    MAINTENANCE: 'Maintenance',
    OTHER: 'Other',
  },
  status: { OPEN: 'Open', DONE: 'Done', DISMISSED: 'Dismissed' },
  filters: { status: 'Status', kind: 'Type', allKinds: 'All types' },
  due: 'Due {{date}}',
  noDue: 'No due date',
  overdue: 'Overdue',
  assignedTo: 'Assigned to {{name}}',
  actions: { complete: 'Complete', dismiss: 'Dismiss', open: 'Open record' },
  emptyTitle: 'Nothing to do',
  emptyBody: 'No open task: today’s file is up to date.',
  dashboard: { title: 'Tasks', seeAll: 'See all tasks', empty: 'No open task.' },
  toasts: { done: 'Task completed.', dismissed: 'Task dismissed.' },
  notifications: {
    title: 'Notifications',
    subtitle: 'Team alerts and reminders.',
    bell: 'Notifications',
    bellWithCount: 'Notifications — {{count}} unread',
    bellWithCount_other: 'Notifications — {{count}} unread',
    markRead: 'Mark as read',
    markAllRead: 'Mark all as read',
    unread: 'Unread',
    emptyTitle: 'No notification',
    emptyBody: 'Compliance and payment alerts will show up here.',
    kind: {
      DOCUMENT_MISSING: 'Missing document',
      TASK_ASSIGNED: 'Task assigned',
      PAYMENT_EXPECTED: 'Payment expected',
      SUPPORT_ACCESS_REQUESTED: 'Support access request',
      SUBSCRIPTION_INVOICE: 'HOREVIA subscription invoice',
      SUBSCRIPTION_OVERDUE: 'HOREVIA subscription overdue',
      OTHER: 'Information',
    },
  },
};

export const billing = {
  /* ---------------------- Sending by email (sprint 2.6) ------------------- */
  send: {
    title: 'Send the document',
    description: 'Send {{document}} to the customer by email.',
    recipient: 'Recipient',
    recipientHint: 'Pre-filled from the billed customer record; editable.',
    recipientInvalid: 'Enter a valid email address.',
    message: 'Message',
    messageHint: 'Added to the email body before the attached document.',
    submit: 'Send',
    channelEmail: 'Email',
    channelWhatsapp: 'WhatsApp',
    channelWhatsappSoon: 'The WhatsApp channel arrives with the Notifications module.',
    soon: 'soon',
    toastSent: '{{document}} sent to {{to}}.',
  },

  document: {
    title: {
      PROFORMA: 'Proforma invoice',
      INVOICE: 'Invoice',
      CREDIT_NOTE: 'Credit note',
    },
    ariaLabel: '{{title}} {{number}}',
    noNumber: 'no number',
    draftNumber: 'Number assigned on issue',
    issuerActivity: 'Furnished apartment rental & meeting room',
    phoneShort: 'Tel. {{phone}}',
    billTo: 'Billed to',
    subject: 'Subject',
    occupant: 'guest: {{name}}',
    currencyName: 'CFA franc ({{code}})',
    meta: {
      placeAndDate: '{{city}}, on',
      dueDate: 'Due date',
      validUntil: 'Offer valid until',
      stay: 'Stay period',
      currency: 'Currency',
      reservation: 'Reservation',
      relatedInvoice: 'Source invoice',
    },
    columns: {
      designation: 'Description',
      unitPrice: 'Unit price',
      quantity: 'Quantity',
      amount: 'Amount',
    },
    totals: {
      subtotal: 'Subtotal',
      discount: 'Commercial discount',
      discountPercent: 'Commercial discount ({{value}}%)',
      discounted: 'Discounted amount',
      vat: 'VAT — CME regime',
      vatNotApplicable: 'Not applicable',
      withholding: 'Withholding tax ({{rate}}%) — on the discounted amount',
      withholdingNotApplied: 'Not applied',
      net: 'Net payable',
      creditTotal: 'Credit note amount',
    },
    words: {
      PROFORMA: 'This proforma invoice is settled at the sum of',
      INVOICE: 'This invoice is settled at the sum of',
      CREDIT_NOTE: 'This credit note is settled at the sum of',
      withholdingNote:
        'Withholding tax of 5% applied to the discounted amount, per the customer obligations.',
    },
    payment: {
      title: 'Payment terms',
      methods: ['Cash', 'Orange Money', 'Moov Money', 'Bank transfer', 'Cheque', 'Card'],
      bank: 'Bank',
      account: 'Account',
      holder: 'Account holder',
    },
    mentions: {
      invoiceTerms:
        'Payment due within 15 days of the invoice date. Late payment may incur penalties under the general terms.',
      proformaNonContractual:
        'Offer valid for 15 days. Non-contractual document — this is not an invoice and cannot be used for accounting or tax purposes.',
      creditNote:
        'This credit note cancels or reduces the source invoice. Keep it with the original invoice for accounting purposes.',
    },
    signature: {
      for: 'For {{name}}',
      box: 'Signature & stamp',
      defaultRole: 'The Manager',
    },
    footer: {
      phone: 'Tel.',
      rccm: 'RCCM',
      regime: 'Regime',
      bank: 'Bank',
      email: 'Email',
      ifu: 'IFU',
      taxDivision: 'Tax division',
      account: 'Account',
    },
    print: 'Print',
    downloadPdf: 'PDF',
    sendEmail: 'Send by email',
    sendSoon: 'Send the document to the customer by email.',
    previewTitle: 'Document preview',
    previewLive: 'Live preview — the document mirrors the draft being edited.',
  },

  status: {
    proforma: {
      DRAFT: 'Draft',
      ISSUED: 'Issued',
      ACCEPTED: 'Accepted',
      EXPIRED: 'Expired',
      CANCELLED: 'Cancelled',
    },
    invoice: {
      DRAFT: 'Draft',
      ISSUED: 'Issued',
      PARTIALLY_PAID: 'Partially paid',
      PAID: 'Paid',
      CANCELLED: 'Cancelled',
    },
    creditNote: { DRAFT: 'Draft', ISSUED: 'Issued' },
    overridden: 'Issued with override',
    overriddenTitle: 'R-001 override — reason: {{reason}} · granted by {{author}}',
    immutable: 'Immutable',
    converted: 'Converted to invoice',
    overdue: '{{count}} d overdue',
    expiresIn: 'Expires in {{count}} d',
    expiresToday: 'Expires today',
    blocked: 'Issue blocked',
  },

  proformas: {
    title: 'Proformas',
    subtitle: 'Quotes and deposit requests — numbering PRO/YYYY/NNNNN, valid {{days}} days.',
    listTitle: 'All proformas',
    create: 'New proforma',
    createFromReservation: 'Create a proforma',
    searchLabel: 'Search a proforma',
    searchPlaceholder: 'Number, customer…',
    countLabel: '{{count}} proforma',
    countLabel_other: '{{count}} proformas',
    emptyTitle: 'No proforma',
    emptyBody: 'Create a proforma from a reservation or from scratch.',
    emptySearchTitle: 'No matching proforma',
    emptySearchBody: 'Change the search or clear the filters.',
    kpi: {
      issued: 'Issued',
      issuedFoot: '{{amount}} quoted',
      accepted: 'Accepted',
      acceptedFoot: 'conversion rate {{rate}}%',
      pending: 'Pending',
      pendingFoot: 'issued offers not accepted yet',
      expired: 'Expired',
      expiredFoot: 'to follow up or archive',
    },
    columns: {
      number: 'Number',
      billingParty: 'Billed customer',
      reservation: 'Reservation',
      issueDate: 'Issued',
      validUntil: 'Valid until',
      net: 'Net payable',
      status: 'Status',
    },
    lifecycle: {
      title: 'Document lifecycle',
      draft: 'Draft',
      issued: 'Issued',
      accepted: 'Accepted',
      converted: 'Converted to invoice',
    },
    convertNotice:
      'Converting creates a linked invoice draft. Issuing the invoice still goes through the R-001 compliance check.',
    convertedNotice: 'This proforma has already been converted into an invoice.',
    seeInvoice: 'Open the invoice',
    detailSubtitle: 'Proforma {{number}} — {{customer}}',
    draftSubtitle: 'Proforma draft — {{customer}}',
  },

  invoices: {
    title: 'Invoices',
    subtitle:
      'Gapless numbering FAC/YYYY/NNNNN — an issued invoice is immutable and only a credit note can cancel it.',
    listTitle: 'All invoices',
    create: 'New invoice',
    createFromReservation: 'Issue the invoice',
    searchLabel: 'Search an invoice',
    searchPlaceholder: 'Number (FAC/…), customer…',
    countLabel: '{{count}} invoice',
    countLabel_other: '{{count}} invoices',
    emptyTitle: 'No invoice',
    emptyBody: 'Invoices are created from a reservation or an accepted proforma.',
    emptySearchTitle: 'No matching invoice',
    emptySearchBody: 'Change the search or clear the filters.',
    kpi: {
      invoiced: 'Invoiced',
      invoicedFoot: '{{count}} issued invoice',
      invoicedFoot_other: '{{count}} issued invoices',
      collected: 'Collected',
      collectedFoot: '{{rate}}% of invoiced',
      due: 'Outstanding',
      dueFoot: 'including {{amount}} overdue',
      blocked: 'Blocked issues',
      blockedFoot: 'incomplete compliance files',
    },
    columns: {
      number: 'Number',
      billingParty: 'Billed customer',
      reservation: 'Reservation',
      issueDate: 'Issued',
      dueDate: 'Due',
      net: 'Net payable',
      balance: 'Balance',
      status: 'Status',
    },
    filters: {
      all: 'All',
      balanceDue: 'Balance due',
      overdue: 'Overdue',
      from: 'Issued from',
      to: 'Issued until',
      status: 'Status',
      allStatuses: 'All statuses',
    },
    immutableBanner: {
      title: 'Immutable document — corrections through a credit note',
      body: 'Since it was issued, this invoice can no longer be modified or deleted. A snapshot, the PDF and its hash are kept (D-004).',
      issuedAt: 'Issued on',
      issuedBy: 'By',
      hash: 'PDF SHA-256 hash',
    },
    overrideBanner: {
      title: 'Invoice issued with an R-001 override',
      body: 'Mandatory documents were missing at issue time. Reason: “{{reason}}”. Override granted by {{author}}.',
      completeFile: 'Complete the file',
    },
    payments: {
      title: 'Allocated payments',
      empty: 'No payment allocated to this invoice.',
      columns: { reference: 'Reference', date: 'Date', method: 'Method', amount: 'Amount' },
      soon: 'Recording a payment arrives in sprint 2.6.',
    },
    creditNotes: {
      title: 'Linked credit notes',
      empty: 'No credit note on this invoice.',
    },
    settlement: {
      title: 'Settlement',
      net: 'Net payable',
      paid: 'Paid',
      balance: 'Balance',
      dueOn: 'Due on {{date}}.',
      overdue: '{{count}} day overdue.',
      overdue_other: '{{count}} days overdue.',
    },
    detailSubtitle: 'Invoice {{number}} — {{customer}}',
    draftSubtitle: 'Invoice draft — {{customer}} · {{reference}} · number assigned on issue',
    numberOnIssue: 'Number assigned on issue',
    numberOnIssueHint:
      'The sequence is locked in the database at issue time to guarantee gapless numbering (ADR-008).',
  },

  creditNotes: {
    title: 'Credit notes',
    subtitle: 'The only way to cancel or correct an issued invoice — numbering AV/YYYY/NNNNN.',
    listTitle: 'All credit notes',
    create: 'Create a credit note',
    issueAction: 'Issue the credit note',
    draftNotice:
      'This credit note is still a draft: no number has been consumed and the invoice is not yet credited. Issue it to make it final.',
    countLabel: '{{count}} credit note',
    countLabel_other: '{{count}} credit notes',
    emptyTitle: 'No credit note',
    emptyBody: 'A credit note is created from an issued invoice.',
    columns: {
      number: 'Number',
      invoice: 'Source invoice',
      billingParty: 'Customer',
      issueDate: 'Issued',
      amount: 'Amount',
      reason: 'Reason',
      status: 'Status',
    },
    drawer: {
      title: 'Create a credit note',
      subtitle: 'Invoice {{number}} — {{customer}} · {{amount}}',
      notice:
        'An issued invoice is immutable: a credit note is the only way to cancel it or correct its amount (D-004).',
      scope: 'Credit note scope',
      full: 'Full credit note — cancels the invoice',
      fullHint: 'The credit note equals the net payable of the invoice.',
      partial: 'Partial credit note — selected lines',
      partialHint: 'Add the lines to credit; the total cannot exceed the invoice.',
      reason: 'Reason',
      reasonPlaceholder: 'e.g. night of 18 September not used — early departure',
      reasonHint: 'Reason required (at least 5 characters), printed on the document and audited.',
      submit: 'Create the credit note',
      exceeds: 'The credit note exceeds the net payable of the invoice ({{amount}}).',
      draftPending:
        'The draft credit note was created but issuing it failed. Try again: only the issuing step is replayed, no second credit note is created.',
    },
    detailSubtitle: 'Credit note {{number}} — invoice {{invoice}}',
  },

  compliance: {
    blockedTitle: 'Invoice issuing refused by the server',
    blockedBody:
      'The final invoice of reservation {{reference}} cannot be issued: the compliance file is INCOMPLETE.',
    blockedRule:
      'This check runs server-side (business rule R-001). Only a Manager or Owner can lift it, through a documented and audited override. Proforma, payments and check-in remain possible.',
    missingTitle: 'Missing mandatory documents',
    missingCount: '{{provided}} of {{total}} provided',
    missing: 'Missing',
    completeFile: 'Complete the file',
    seeReservation: 'Open the reservation',
    retry: 'Retry issuing',
    overrideAvailable:
      'You are signed in as {{name}}: the invoice.issue.override_compliance override is available to you.',
    overrideUnavailable: 'The R-001 override is restricted to Manager and Owner profiles (D-026).',
    unblockTitle: 'What unblocks the issue',
    unblockStep1: 'Record the missing documents from the check-in screen.',
    unblockStep2: 'The compliance status automatically turns COMPLETE.',
    unblockStep3: 'Issue the final invoice: number, PDF and hash are frozen.',
    goToCheckIn: 'Go to check-in',
    alreadyInvoiced: 'A final invoice already exists for this reservation.',
    statusBlocked: 'The current reservation status does not allow issuing a final invoice.',
    override: {
      action: 'Issue with override',
      title: 'Override of the compliance rule',
      subtitle: '{{reference}} — {{customer}} · {{amount}}',
      notice:
        'Rule R-001 forbids issuing a final invoice while a mandatory document is missing. The override is restricted to Manager and Owner profiles; it is audited and stays visible on the issued invoice.',
      reason: 'Override reason',
      reasonPlaceholder:
        'e.g. corporate guest left before handing over the police form — documents expected within 48 h',
      reasonHint:
        'The reason appears in the audit log and on the invoice. At least 20 characters — {{count}}/20.',
      commitment: 'I confirm the documents will be collected',
      commitmentHint:
        'A follow-up task will be created for the front desk, due 48 h after issuing.',
      auditNotice:
        'The invoice.issue.override_compliance event will be recorded with your identity, the timestamp and the reason. The issued invoice will carry the “Issued with override” mention.',
      submit: 'Issue the invoice with override',
      expectedBy: 'Documents expected by',
      denied:
        'Override refused by the server: the invoice.issue.override_compliance permission is restricted to Manager and Owner profiles (D-026).',
    },
  },

  editor: {
    title: 'Document content',
    linesTitle: 'Billed services',
    addLine: 'Add a line',
    removeLine: 'Remove the line',
    lineLabel: 'Description',
    lineDescription: 'Details',
    lineQuantity: 'Quantity',
    lineUnitPrice: 'Unit price',
    lineAmount: 'Amount',
    lineKind: 'Kind',
    kind: {
      ACCOMMODATION: 'Accommodation',
      EXTRA: 'Extra',
      FEE: 'Fee',
      DISCOUNT: 'Discount',
    },
    emptyLines: 'No line: add at least one service before issuing the document.',
    discount: 'Commercial discount',
    discountType: 'Discount type',
    discountValue: 'Value',
    discountTypes: { NONE: 'None', PERCENT: 'Percentage', AMOUNT: 'Fixed amount' },
    withholding: 'Apply withholding tax ({{rate}}%)',
    withholdingHint:
      'Computed on the discounted amount, never on the gross subtotal. Enabled document by document, per the billed customer obligations.',
    withholdingBase: 'Base: discounted amount {{amount}}',
    subject: 'Document subject',
    subjectPlaceholder: 'Rental of apartment F2 – 111 from 15 to 20 September 2026',
    billingParty: 'Billed customer',
    property: 'Issuing property',
    reservation: 'Linked reservation',
    noReservation: 'None (free entry)',
    validUntil: 'Offer validity',
    dueDate: 'Due date',
    notes: 'Specific mentions',
    stayFrom: 'Stay start',
    stayTo: 'Stay end',
    save: 'Save the draft',
    saved: 'Draft saved.',
    unsaved: 'Unsaved changes',
    readOnlyTitle: 'Read-only document',
    readOnlyBody: 'This document can no longer be edited in its current state.',
  },

  actions: {
    issue: 'Issue',
    issueProforma: 'Issue the proforma',
    issueInvoice: 'Issue the invoice',
    accept: 'Mark accepted',
    cancel: 'Cancel',
    convert: 'Convert to invoice',
    createCreditNote: 'Create a credit note',
    recordPayment: 'Record a payment',
    paymentSoon: 'Record a payment against this invoice.',
    backToList: 'Back to the list',
  },
  dialogs: {
    issueProformaTitle: 'Issue the proforma?',
    issueProformaBody:
      'The proforma will receive a PRO/{{year}}/NNNNN number and its content will be frozen. It can still be cancelled without a credit note.',
    issueInvoiceTitle: 'Issue the invoice?',
    issueInvoiceBody:
      'The invoice will receive a FAC/{{year}}/NNNNN number and become immutable. Any correction will then go through a credit note.',
    acceptTitle: 'Mark the proforma as accepted?',
    acceptBody:
      'Acceptance unlocks the conversion into an invoice. Set the acceptance date if it differs from today.',
    acceptedAt: 'Acceptance date',
    cancelTitle: 'Cancel the proforma?',
    cancelBody: 'A cancelled proforma stays readable; no credit note is required.',
    cancelReason: 'Cancellation reason',
    cancelReasonPlaceholder: 'e.g. the customer gave up the stay',
    convertTitle: 'Convert to invoice?',
    convertBody:
      'An invoice draft linked to this proforma will be created with the same lines. Issuing stays subject to R-001 compliance.',
    dueDate: 'Invoice due date',
  },
  toasts: {
    proformaCreated: 'Proforma created.',
    proformaUpdated: 'Proforma updated.',
    proformaIssued: 'Proforma {{number}} issued.',
    proformaAccepted: 'Proforma marked as accepted.',
    proformaCancelled: 'Proforma cancelled.',
    proformaConverted: 'Invoice draft created.',
    invoiceCreated: 'Invoice draft created.',
    invoiceUpdated: 'Invoice updated.',
    invoiceIssued: 'Invoice {{number}} issued.',
    invoiceIssuedWithOverride: 'Invoice {{number}} issued with override — the event was audited.',
    creditNoteCreated: 'Credit note created.',
    creditNoteIssued: 'Credit note {{number}} issued.',
    settingsSaved: 'Billing settings saved.',
    pdfOpened: 'PDF opened in a new tab.',
  },

  reservationTab: {
    title: 'Billing',
    summaryTitle: 'Financial summary',
    deposits: 'Deposits',
    invoiced: 'Invoiced',
    paid: 'Paid',
    balance: 'Balance',
    proformasTitle: 'Reservation proformas',
    invoicesTitle: 'Reservation invoices',
    noProformas: 'No proforma for this reservation.',
    noInvoices: 'No invoice for this reservation.',
    createProforma: 'Create a proforma',
    createInvoice: 'Issue the invoice',
    openInvoice: 'Open the invoice',
  },
  customerTab: {
    invoicesTitle: 'Customer invoices',
    empty: 'No invoice issued for this customer.',
    balance: 'Customer balance',
  },

  settings: {
    title: 'Billing',
    subtitle:
      'Proforma validity, due dates, place of issue, signatory and legal mentions of documents.',
    proformaValidityDays: 'Proforma validity (days)',
    invoiceDueDays: 'Invoice due delay (days)',
    placeOfIssue: 'Place of issue',
    signatoryName: 'Document signatory',
    proformaTermsText: 'Proforma mention',
    invoicePaymentTermsText: 'Invoice payment terms',
    paymentMethodsText: 'Accepted payment methods',
    footerNote: 'Footer note (optional)',
    numberingTitle: 'Numbering formats',
    numberingHint:
      'Sequences per fiscal year, consumed on issue and gapless (ADR-008). A new format applies to sequences created afterwards (next fiscal year); issued numbers never change.',
    numberingExample: 'Tokens: {YYYY} = fiscal year, {NNNNN} = 5-digit counter.',
    numberingProforma: 'Proformas',
    numberingInvoice: 'Invoices',
    numberingCreditNote: 'Credit notes',
    numberingOrder: 'POS orders',
    numberingPurchase: 'Purchase orders (stock)',
    numberingCount: 'Stock counts',
    resetYearly: 'Yearly reset',
    resetYearlyOn: 'Yes — the sequence restarts at 1 every year',
    resetYearlyOff: 'No — continuous sequence',
    save: 'Save',
  },
};

/* -------------------------------------------------------------------------- */
/* Sprint 2.6 — Payments                                                       */
/* -------------------------------------------------------------------------- */

export const payments = {
  list: {
    title: 'Payments',
    subtitle:
      'Collections, invoice allocations and receipts — cash, mobile money, transfer, cheque and card terminal.',
    tableTitle: 'All payments',
    countLabel: '{{count}} payment',
    countLabel_other: '{{count}} payments',
    searchLabel: 'Search a payment',
    searchPlaceholder: 'PAY-2026-000045, transaction reference, customer…',
    emptyTitle: 'No payment recorded',
    emptyBody: 'Record a payment from an invoice, a reservation or the “Record a payment” button.',
    emptySearchTitle: 'No payment matches',
    emptySearchBody: 'Change the search or clear the filters.',
    noCustomer: 'Customer not set',
    notAllocated: 'Unallocated',
    toAllocate: 'To allocate · {{amount}}',
    periodTotal: 'Period total',
    filters: {
      unallocated: 'Unallocated',
      from: 'From',
      to: 'To',
    },
  },
  kpi: {
    today: 'Collected today',
    todayFoot: '{{count}} operation',
    todayFoot_other: '{{count}} operations',
    month: 'Collected this month',
    monthFoot: 'since the 1st of the month',
    monthCount: '{{count}} settlement since the 1st',
    monthCount_other: '{{count}} settlements since the 1st',
    unallocatedCount: '{{count}} payment to allocate',
    unallocatedCount_other: '{{count}} payments to allocate',
    byMethodTitle: 'Split by payment method',
    byMethodFoot: 'current month',
    byMethodCount: '{{count}} settlement',
    byMethodCount_other: '{{count}} settlements',
    unallocated: 'Unallocated',
    unallocatedFoot: 'to spread over invoices',
    refunds: 'Refunds',
    refundsFoot: 'for the current month',
    cashOpen: 'Cash drawer open · {{amount}}',
  },
  columns: {
    reference: 'Reference',
    date: 'Date',
    customer: 'Customer',
    method: 'Method',
    allocation: 'Allocation',
    amount: 'Amount',
    status: 'Status',
  },
  method: {
    CASH: 'Cash',
    MOBILE_MONEY: 'Mobile money',
    BANK_TRANSFER: 'Bank transfer',
    CHEQUE: 'Cheque',
    CARD: 'Card terminal',
    OTHER: 'Other',
  },
  provider: {
    ORANGE_MONEY: 'Orange Money',
    MOOV_MONEY: 'Moov Money',
    WAVE: 'Wave',
    CORIS_MONEY: 'Coris Money',
    CORIS_BANK: 'Coris Bank International',
    ECOBANK: 'Ecobank',
    UBA: 'UBA',
    OTHER: 'Other institution',
  },
  status: {
    RECORDED: 'Recorded',
    CONFIRMED: 'Confirmed',
    REVERSED: 'Voided',
    REFUND: 'Refund',
  },
  kind: {
    DEPOSIT: 'Deposit',
    PAYMENT: 'Payment',
    REFUND: 'Refund',
  },
  drawer: {
    title: 'Record a payment',
    subtitleInvoice: '{{number}} — {{customer}} · balance {{balance}}',
    subtitleReservation: '{{reference}} — {{customer}} · balance {{balance}}',
    subtitleFree: 'Free entry — allocate to an invoice or a reservation.',
    offline: 'A connection is required to record a payment.',
    successTitle: 'Payment recorded',
    successBody: '{{amount}} collected · {{reference}}.',
    successUnallocated: '{{amount}} remain unallocated: spread them from the payment page.',
    effect: 'This payment will settle {{documents}} as of {{date}}.',
  },
  actions: {
    record: 'Record a payment',
    downloadReceipt: 'Download the receipt',
    sendReceipt: 'Send the receipt',
    openPayment: 'Open the payment page',
    generateReceipt: 'Generate the receipt',
    refund: 'Refund',
    void: 'Void the payment',
    editAllocations: 'Edit allocations',
  },
  fields: {
    amount: 'Amount received',
    amountHint: 'Outstanding balance: {{balance}}.',
    amountHintFree: 'Strictly positive amount, in CFA francs (no decimals).',
    suggestBalance: 'Balance',
    suggestHalf: 'Half',
    method: 'Payment method',
    provider: 'Operator / institution',
    reference: 'Transaction reference',
    referenceHint: 'Orange Money number, cheque number or transfer reference.',
    chequeNumber: 'Cheque number',
    bankName: 'Drawee bank',
    paidAt: 'Date and time of collection',
    notes: 'Internal note',
    notesPlaceholder: 'Context of the collection, contact person…',
    proof: 'Proof',
    proofAdd: 'Attach a proof',
    proofTitle: 'Orange Money screenshot, deposit slip, card terminal receipt…',
    proofAttached: 'Proof attached',
    cashSession: 'Cash drawer open — expected in drawer: {{balance}}.',
  },
  allocation: {
    legend: 'Payment allocation',
    hint: 'Spread the amount received across this customer’s open documents.',
    none: 'No open document: the payment stays unallocated and can be spread later.',
    outstanding: 'Outstanding: {{amount}}',
    remaining: 'Left to allocate',
    toggle: 'Allocate to {{label}}',
    amountFor: 'Amount allocated to {{label}}',
    unallocatedWarning:
      '{{amount}} will stay unallocated (overpayment). The payment will show as “to allocate”.',
  },
  errors: {
    amountRequired: 'Enter a strictly positive amount.',
    referenceRequired: 'Enter the transaction reference.',
    chequeNumberRequired: 'Enter the cheque number.',
    bankNameRequired: 'Enter the drawee bank.',
    reasonTooShort: 'The reason must be at least 5 characters long.',
    cashSessionNotOpenTitle: 'No cash session open',
  },
  toasts: {
    recorded: 'Payment of {{amount}} recorded · {{reference}}.',
    refunded: 'Refund of {{amount}} recorded · {{reference}}.',
    voided: 'Payment voided: a compensating entry has been created.',
    allocationsUpdated: 'Allocations updated.',
    receiptGenerated: 'Receipt {{number}} generated.',
  },
  detail: {
    subtitle: '{{customer}} · collected on {{date}}',
    allocationsTitle: 'Allocations',
    allocationsEmptyTitle: 'Unallocated payment',
    allocationsEmptyBody:
      'This amount is not linked to any invoice or reservation. Edit the allocations to spread it.',
    allocationDocument: 'Document',
    historyTitle: 'History',
    appendOnly: 'Append-only log',
    eventRecorded: 'Payment recorded',
    eventVoided: 'Payment voided',
    summaryTitle: 'Details',
    allocated: 'Allocated',
    unallocated: 'Unallocated',
    refunded: 'Already refunded',
    refundable: 'Refundable',
    recordedBy: 'Recorded by',
    openCashSession: 'Open the session',
    proofAttached: 'A proof is attached to this payment.',
    reversedTitle: 'Payment voided',
    reversedBody: 'Reason: {{reason}} — voided by {{author}} on {{date}}.',
    unallocatedNotice:
      '{{amount}} of this payment is not allocated to any document. Spread it to settle the relevant invoices.',
  },
  refund: {
    title: 'Refund a payment',
    subtitle: '{{reference}} — available: {{available}}',
    amount: 'Refunded amount',
    amountHint: 'At most {{available}} (amount collected minus refunds already issued).',
    method: 'Refund method',
    reason: 'Refund reason',
    reasonHint: 'The reason is logged and shown on the payment page.',
    reasonPlaceholder: 'Stay cancelled at the customer’s request…',
    submit: 'Record the refund',
  },
  void: {
    title: 'Void the payment',
    description: '{{reference}} — this operation is final.',
    warning:
      'A payment is never deleted: voiding creates a traced compensating entry. The related invoices will go back to partially paid or unpaid.',
    reason: 'Reason for voiding',
    reasonHint: 'At least 5 characters, logged with your name.',
    confirmLabel: 'Type “{{reference}}” to confirm',
  },
};

/* -------------------------------------------------------------------------- */
/* Sprint 2.6 — Cash                                                           */
/* -------------------------------------------------------------------------- */

export const cash = {
  title: 'Cash drawer',
  subtitle: 'Front-desk cash session: float, collections, payouts, closing and reconciliation.',
  closedTitle: 'Cash drawer closed',
  closedBody: 'No session is open: cash payments will be refused until the drawer is opened.',
  openedAt: 'opened at {{time}} by {{author}}',
  movementsTitle: 'Movements of the day',
  movementsCount: '{{count}} movement',
  movementsCount_other: '{{count}} movements',
  movementsEmptyTitle: 'No movement',
  movementsEmptyBody: 'Cash collections and payouts will show up here.',
  expensesNoticeTitle: 'Expenses are recorded in Expense tracking',
  expensesNoticeBody:
    'A cash expense creates its own payout in the open session; the drawer no longer offers an “Expense” movement (cash in, cash out and adjustment remain).',
  expenseLink: 'Expense {{reference}}',
  expenseLinkUnknown: 'Expense recorded in Expense tracking',
  historyTitle: 'Session history',
  historyEmptyTitle: 'No cash session',
  status: {
    OPEN: 'Open',
    CLOSED: 'Closed',
    reconciled: 'Variance justified',
  },
  fields: {
    openingBalance: 'Float',
    ins: 'Cash in',
    outs: 'Cash out',
    expenses: 'Expenses',
    refunds: 'Refunds',
    payments: 'Cash collections',
    expectedBalance: 'Expected in drawer',
    actualBalance: 'Counted',
    variance: 'Variance',
    notes: 'Notes',
    closedBy: 'Closed by',
  },
  kpi: {
    paymentsFoot: 'including {{amount}} of collections',
    expensesFoot: 'including {{amount}} of expenses',
  },
  columns: {
    time: 'Time',
    kind: 'Type',
    description: 'Label',
    amount: 'Amount',
    openedAt: 'Opening',
    openedBy: 'Opened by',
    opening: 'Float',
    expected: 'Expected',
    counted: 'Counted',
    variance: 'Variance',
    status: 'Status',
  },
  movementKind: {
    IN: 'Cash in',
    OUT: 'Cash out',
    EXPENSE: 'Expense',
    ADJUSTMENT: 'Adjustment',
    PAYMENT: 'Collection',
    REFUND: 'Refund',
  },
  category: {
    APPORT: 'Cash contribution',
    REMBOURSEMENT_FOURNISSEUR: 'Supplier refund',
    REMISE_BANQUE: 'Bank deposit',
    AVANCE_PERSONNEL: 'Staff advance',
    FOURNITURES: 'Supplies',
    CARBURANT: 'Generator fuel',
    ENTRETIEN: 'Maintenance and repairs',
    TRANSPORT: 'Transport',
    CORRECTION_SAISIE: 'Data-entry correction',
    AUTRE: 'Other',
  },
  actions: {
    openSession: 'Open the drawer',
    closeSession: 'Close the drawer',
    recordMovement: 'Movement',
    reconcile: 'Justify the variance',
    downloadReport: 'PDF report',
  },
  open: {
    title: 'Open a cash session',
    description: 'The opening float is the basis of the expected balance.',
    openingBalance: 'Float',
    openingBalanceHint: 'Cash present in the drawer at opening (0 accepted).',
  },
  movement: {
    title: 'Cash movement',
    description: 'Expected in drawer before the movement: {{balance}}.',
    kind: 'Movement type',
    category: 'Category',
    direction: 'Adjustment direction',
    directionIn: 'Cash in',
    directionOut: 'Cash out',
    descriptionLabel: 'Label',
    descriptionPlaceholder: 'Light bulbs — Gounghin hardware store',
    amount: 'Amount',
    submit: 'Record the movement',
  },
  close: {
    title: 'Close the cash session',
    description: 'Count the cash present, then enter the actual amount.',
    counted: 'Counted amount',
    countedHint: 'Total of notes and coins actually in the drawer.',
    variance: 'Variance (actual − expected)',
    varianceWarning:
      'A variance is found: explain it here. The formal justification then goes through reconciliation.',
  },
  reconcile: {
    title: 'Justify a cash variance',
    description: 'Variance found at closing: {{amount}}.',
    justification: 'Variance justification',
    justificationHint: 'At least 5 characters, logged with your name (cash.reconcile permission).',
    justificationPlaceholder: 'Change error on payment PAY-2026-000045…',
    pendingTitle: 'Unjustified cash variance',
    pendingBody: 'Variance of {{amount}} on the session closed on {{date}}.',
  },
  errors: {
    amountRequired: 'Enter a strictly positive amount.',
    descriptionRequired: 'Enter a label (2 characters minimum).',
    countRequired: 'Enter the counted amount.',
    notesRequired: 'Explain the variance found (5 characters minimum).',
    justificationRequired: 'The justification must be at least 5 characters long.',
  },
  toasts: {
    sessionOpened: 'Cash session opened.',
    movementRecorded: 'Movement recorded.',
    sessionClosed: 'Session closed with no variance.',
    sessionClosedWithVariance: 'Session closed with a variance of {{amount}}.',
    reconciled: 'Variance justified and reconciled.',
  },
  detail: {
    title: 'Session of {{date}}',
    subtitle: 'Opened by {{author}}',
    journalTitle: 'Movement journal',
    summaryTitle: 'Summary',
    justificationTitle: 'Variance justified',
    justificationBody: '{{justification}} — reconciled by {{author}}.',
  },
};

export const analytics = {
  period: {
    label: 'Analysis period',
    from: 'Period start',
    to: 'Period end',
    range: 'From {{from}} to {{to}}',
    compare: 'Compare with previous period',
    presets: {
      TODAY: 'Today',
      LAST_7_DAYS: '7 days',
      THIS_MONTH: 'This month',
      LAST_MONTH: 'Last month',
      THIS_QUARTER: 'This quarter',
      THIS_YEAR: 'This year',
      CUSTOM: 'Custom',
    },
  },
  delta: {
    points: '{{value}} pts',
    aria: {
      up: 'Up {{value}} compared with the previous period',
      down: 'Down {{value}} compared with the previous period',
      flat: 'Unchanged compared with the previous period',
    },
  },
  performance: {
    title: 'Performance',
    noPermission: 'Activity indicators require the “Operational reports” permission.',
  },
  finance: {
    title: 'Finance',
    badge: '{{from}} → {{to}}',
    invoiced: 'Invoiced',
    collected: 'Collected',
    outstanding: 'Outstanding',
    overdue: 'Overdue',
    deposits: 'Deposits received',
    refunds: 'Refunds',
    expenses: 'Expenses',
    seeExpenses: 'See expenses',
    seeInvoices: 'View invoices',
    seePayments: 'View payments',
    seeOutstanding: 'View partially paid invoices',
    seeOverdue: 'View overdue invoices',
    seeReports: 'Open reports',
  },
  kpi: {
    revenue: 'Revenue',
    revenueFoot: 'over {{count}} day',
    revenueFoot_other: 'over {{count}} days',
    occupancy: 'Occupancy rate',
    occupancyFoot: 'bookable units occupied',
    adr: 'Average daily rate',
    adrFoot: 'per night sold',
    revpar: 'RevPAR',
    revparFoot: 'per available unit',
    averageStay: 'Average length of stay',
    averageStayFoot: 'per reservation',
    nightsUnit: 'nights',
    reservations: 'Reservations created',
    reservationsFoot: 'over the period',
    cancelled: 'Cancellations',
    cancelledFoot: 'including {{count}} no-show',
    noShow: 'No-shows',
    noShowFoot: 'arrivals not honoured',
  },
  charts: {
    dailyTitle: 'Daily activity',
    dailyDescription:
      'Revenue and collections per day from {{from}} to {{to}}: {{revenue}} invoiced, {{collected}} collected. Figures are available in the “Show data” table.',
    dailyTableCaption: 'Revenue, collections and occupancy per day',
    revenue: 'Revenue',
    collected: 'Collected',
    collectedHidden: 'collections hidden (permission required)',
    occupancy: 'Occupancy rate',
    thousands: 'in thousands of {{currency}}',
    thousandsShort: 'k{{currency}}',
    showData: 'Show data',
    byResourceTitle: 'Revenue by resource',
    byResourceDescription:
      'Ranking of the {{count}} bookable resources by revenue for the period. Figures are available in the “Show data” table.',
    bySourceTitle: 'Revenue by channel',
    bySourceDescription:
      'Revenue split across {{count}} booking channels. Figures are available in the “Show data” table.',
    resourceSub: '{{nights}} nights · {{rate}} % occupancy',
    columns: {
      date: 'Day',
      resource: 'Resource',
      source: 'Channel',
      nights: 'Nights',
      reservations: 'Reservations',
    },
  },
  empty: {
    title: 'No activity in this period',
    body: 'No billable stay covers the selected dates. Widen the period.',
  },
};

export const search = {
  placeholder: 'Search a reservation, a customer, an invoice…',
  searching: 'Searching…',
  tooShort: 'Type at least {{count}} characters.',
  resultCount: '{{count}} result',
  resultCount_other: '{{count}} results',
  noResults: 'No result for “{{query}}”.',
  groups: {
    RESERVATION: 'Reservations',
    CUSTOMER: 'People',
    COMPANY: 'Companies',
    INVOICE: 'Invoices',
    PROFORMA: 'Proformas',
    PAYMENT: 'Payments',
    RESOURCE: 'Resources',
    EXPENSE: 'Expenses',
  },
  status: {
    DRAFT: 'Draft',
    PENDING_CONFIRMATION: 'Pending',
    OPTION: 'Option',
    CONFIRMED: 'Confirmed',
    CHECKED_IN: 'In house',
    CHECKED_OUT: 'Checked out',
    CANCELLED: 'Cancelled',
    NO_SHOW: 'No-show',
    ISSUED: 'Issued',
    PAID: 'Paid',
    PARTIALLY_PAID: 'Partially paid',
    ACCEPTED: 'Accepted',
    EXPIRED: 'Expired',
    PENDING: 'Pending',
    SETTLED: 'Settled',
    REVERSED: 'Reversed',
    ARCHIVED: 'Archived',
    AVAILABLE: 'Available',
    OCCUPIED: 'Occupied',
    DIRTY: 'Dirty',
    CLEANING: 'Cleaning',
    READY: 'Ready',
    MAINTENANCE: 'Maintenance',
    BLOCKED: 'Blocked',
  },
};

export const reports = {
  title: 'Reports',
  subtitle: 'Twelve activity, finance, customer and resource reports, exportable to CSV.',
  open: 'Open report',
  exportHint: 'Pick a report, then “Export CSV”.',
  emptyTitle: 'No report available',
  emptyBody: 'Your role gives access to no report. Contact the tenant owner.',
  emptyResultTitle: 'No data for this period',
  emptyResultBody: 'Widen the period or clear the status filter.',
  filtersTitle: 'Filters',
  tableTitle: 'Results',
  groupBy: 'Group by',
  statusFilter: 'Status',
  totals: 'Total',
  totalsFoot: '{{count}} row',
  totalsFoot_other: '{{count}} rows',
  generatedAt: '{{count}} row',
  generatedAt_other: '{{count}} rows',
  families: {
    ACTIVITY: 'Activity',
    SALES: 'Sales & stock',
    FINANCE: 'Finance',
    CUSTOMERS: 'Customers',
    RESOURCES: 'Resources',
  },
  groupByOptions: {
    DAY: 'Day',
    WEEK: 'Week',
    MONTH: 'Month',
    RESOURCE: 'Resource',
    RESOURCE_TYPE: 'Resource type',
    SOURCE: 'Channel',
    CUSTOMER: 'Customer',
    COMPANY: 'Company',
    METHOD: 'Payment method',
    CATEGORY: 'Category',
    OUTLET: 'Outlet',
    PRODUCT: 'Product',
    SERVER: 'Server',
    HOUR: 'Hour',
    WEEKDAY: 'Weekday',
    SERVICE_TYPE: 'Service type',
    ITEM: 'Item',
    MODULE: 'Family',
  },
  scope: {
    label: 'Scope',
    ENGAGED: 'Engaged',
    ALL: 'All',
    CANCELLED: 'Cancelled',
    hint: 'Engaged = confirmed, checked-in or checked-out. Cancellations and no-shows only add up under “All” or “Cancelled”.',
  },
  actions: {
    exportCsv: 'Export CSV',
    print: 'Print',
  },
  table: {
    caption: '{{kind}} report — {{count}} rows',
    sortBy: 'Sort by {{column}}',
  },
  toasts: {
    exported: 'Export “{{name}}” ready.',
  },
  status: {
    DRAFT: 'Draft',
    PENDING_CONFIRMATION: 'Pending',
    OPTION: 'Option',
    CONFIRMED: 'Confirmed',
    CHECKED_IN: 'In house',
    CHECKED_OUT: 'Checked out',
    CANCELLED: 'Cancelled',
    NO_SHOW: 'No-show',
    ISSUED: 'Issued',
    PAID: 'Paid',
    PARTIALLY_PAID: 'Partially paid',
    OVERDUE: 'Overdue',
    PENDING: 'Pending',
    SETTLED: 'Settled',
    REVERSED: 'Reversed',
  },
  kinds: {
    REVENUE: {
      title: 'Revenue',
      description:
        'Service revenue by day, week, month, resource or channel, with invoiced and collected.',
    },
    OCCUPANCY: {
      title: 'Occupancy',
      description: 'Offered and occupied nights, occupancy rate by day, resource or resource type.',
    },
    RESERVATIONS: {
      title: 'Reservations',
      description: 'Reservations created over the period, by status and channel.',
    },
    CUSTOMERS: {
      title: 'Customers',
      description: 'Nights and revenue per hosted person.',
    },
    COMPANIES: {
      title: 'Companies',
      description: 'Nights and revenue per invoiced company.',
    },
    PAYMENTS: {
      title: 'Payments',
      description: 'Settlements by day or method (cash, mobile money, transfer…).',
    },
    RECEIVABLES: {
      title: 'Receivables',
      description: 'Unsettled invoices, days overdue and balance per customer.',
    },
    DISCOUNTS: {
      title: 'Discounts',
      description: 'Discounts granted per invoice, in amount and rate.',
    },
    TAXES: {
      title: 'Taxes & withholding',
      description: 'Taxes and the 5 % withholding tax per period (R-012).',
    },
    RESOURCE_PERFORMANCE: {
      title: 'Resource performance',
      description: 'Revenue, nights and occupancy per unit or meeting room.',
    },
    CASH: {
      title: 'Cash',
      description: 'Cash settlements per day, for cash reconciliation.',
    },
    EXPENSES: {
      title: 'Expenses',
      description:
        'Disbursements per day, week, month, payment method or category (cancellations excluded).',
    },
    SALES: {
      title: 'Sales',
      description:
        'Paid and stay-charged orders by outlet, category, product, hour, weekday, server, service type or period.',
    },
    CONSOLIDATED_REVENUE: {
      title: 'Consolidated revenue',
      description:
        'Revenue by family (accommodation, restaurant, bar…) or by period, every sale counted once.',
    },
    PRODUCT_MARGIN: {
      title: 'Margins by product',
      description:
        'Revenue excl. tax, cost of sales at stock issue and gross margin of each product sold.',
    },
    STOCK_CONSUMPTION: {
      title: 'Stock consumption',
      description: 'Quantities and costs consumed, purchased and lost by item, category or period.',
    },
    CUSTOMER_ACTIVITY: {
      title: 'Customer activity',
      description:
        'Visits, stays, orders and spend of each customer over the period, tier and points balance.',
    },
    LOYALTY: {
      title: 'Loyalty',
      description: 'Points earned, redeemed and adjusted by day, week, month or customer.',
    },
  },
};

export const expenses = {
  title: 'Expenses',
  subtitle:
    'Tracking of the property’s disbursements — cash, mobile money, cheque, bank transfer and bank withdrawal.',
  list: {
    tableTitle: 'Expenses of the period',
    countLabel: '{{count}} expense',
    countLabel_other: '{{count}} expenses',
    searchLabel: 'Search an expense',
    searchPlaceholder: 'EXP-2026-000003, beneficiary, label…',
    emptyTitle: 'No expense for this period',
    emptyBody: 'Record an expense with the “New expense” button or widen the period.',
    emptySearchTitle: 'No expense matches',
    emptySearchBody: 'Change the search or clear the filters.',
    periodTotal: 'Displayed total',
    filters: {
      category: 'Category',
      allCategories: 'All categories',
      status: 'Status',
    },
  },
  kpi: {
    total: 'Period total',
    totalFoot: '{{count}} expense recorded',
    totalFoot_other: '{{count}} expenses recorded',
    cash: 'Cash (drawer payouts)',
    mobileMoney: 'Mobile money',
    bank: 'Cheque, transfer, withdrawal',
    byMethodTitle: 'By payment method',
    byCategoryTitle: 'By category',
    byFoot: 'Selected period, cancelled expenses excluded.',
    count: '{{count}} operation',
    count_other: '{{count}} operations',
  },
  columns: {
    reference: 'Reference',
    date: 'Date',
    beneficiary: 'Beneficiary',
    category: 'Category',
    method: 'Method',
    amount: 'Amount',
    status: 'Status',
    recordedBy: 'Recorded by',
  },
  method: {
    CASH: 'Cash',
    MOBILE_MONEY: 'Mobile money',
    CHEQUE: 'Cheque',
    BANK_TRANSFER: 'Bank transfer',
    BANK_WITHDRAWAL: 'Bank withdrawal',
  },
  provider: {
    ORANGE_MONEY: 'Orange Money',
    MOOV_MONEY: 'Moov Money',
    WAVE: 'Wave',
    OTHER: 'Other operator',
  },
  category: {
    SUPPLIES: 'Supplies',
    FUEL: 'Fuel',
    MAINTENANCE: 'Maintenance and repairs',
    TRANSPORT: 'Transport',
    UTILITIES: 'Water, electricity, internet',
    SALARY_ADVANCE: 'Salary advance',
    TAXES_FEES: 'Taxes and fees',
    BANK_DEPOSIT: 'Bank deposit',
    OTHER: 'Other',
  },
  status: {
    RECORDED: 'Recorded',
    CANCELLED: 'Cancelled',
  },
  actions: {
    record: 'New expense',
    submit: 'Record the expense',
    cancel: 'Cancel the expense',
    confirmCancel: 'Confirm cancellation',
    exportCsv: 'Export CSV',
    openExpense: 'Open the expense',
    openCash: 'Open the cash drawer',
    viewCashSession: 'View the cash session',
    viewExpenses: 'View expenses',
  },
  drawer: {
    title: 'New expense',
    subtitle:
      'A disbursement is numbered EXP-YYYY-NNNNNN and is never deleted: it is cancelled with a reason.',
    cashWarningTitle: 'Cash expense',
    cashWarning:
      'Requires an open cash session: the expense itself creates the payout in the current session.',
    cashOpenBalance: 'Drawer open — expected in drawer: {{balance}}.',
    offline: 'Offline: recording an expense will be possible once the connection is back.',
  },
  fields: {
    expenseDate: 'Expense date',
    amount: 'Amount',
    amountHint: 'Amount paid out, in CFA francs.',
    method: 'Payment method',
    provider: 'Mobile money operator',
    chequeNumber: 'Cheque number',
    reference: 'Transaction reference',
    referenceHint: 'Optional: Orange Money reference, transfer or withdrawal number.',
    category: 'Category',
    beneficiary: 'Beneficiary or supplier',
    beneficiaryPlaceholder: 'Total station, Ouaga 2000',
    description: 'Label',
    descriptionPlaceholder: 'Generator fuel — SONABEL outage of 6 September',
  },
  errors: {
    amountRequired: 'Enter a strictly positive amount.',
    dateRequired: 'Enter the expense date.',
    providerRequired: 'Choose the mobile money operator.',
    chequeNumberRequired: 'The cheque number is required.',
    descriptionRequired: 'Enter a label (3 characters minimum).',
    reasonRequired: 'The reason must be at least 5 characters long.',
  },
  cancel: {
    title: 'Cancel expense {{reference}}',
    description:
      'The expense stays visible with the “Cancelled” status; the reason is logged with your name.',
    cashNotice: 'Cash expense: the cancellation creates the reverse movement in the open drawer.',
    reason: 'Cancellation reason',
    reasonPlaceholder: 'Duplicate entry, wrong amount, supplier invoice cancelled…',
  },
  detail: {
    subtitle: '{{category}} · {{date}}',
    summaryTitle: 'Details',
    cashTitle: 'Cash drawer',
    cashBody: 'Payout created in the session of {{date}}.',
    cashBodyNoSession: 'Payout linked to the original cash session.',
    cancelledTitle: 'Expense cancelled',
    cancelledBody: '{{reason}} — {{author}}, on {{date}}.',
    recordedBy: 'Recorded by',
    recordedAt: 'Recorded on',
    transactionReference: 'Reference',
    noBeneficiary: 'No beneficiary given',
    historyTitle: 'History',
    eventRecorded: 'Expense recorded',
    eventCancelled: 'Expense cancelled',
  },
  toasts: {
    recorded: 'Expense {{reference}} recorded ({{amount}}).',
    cancelled: 'Expense {{reference}} cancelled.',
    exported: 'Export “{{name}}” ready.',
  },
};

export const taxes = {
  title: 'Taxes and withholdings',
  subtitle:
    'Tax rules applied to proformas and invoices. A change of tax regime (VAT) is declared here, with no technical intervention.',
  create: 'New rule',
  kindHelp:
    'Tax: added to the amount due (e.g. VAT). Withholding: deducted (e.g. 5% withholding tax).',
  noDeleteHelp:
    'A rule is never deleted: documents already issued refer to it. Deactivate it to stop applying it to new documents.',
  help: {
    title: 'Charging VAT one day?',
    body: 'If your tax regime charges no VAT (for example CME), no rule is needed. When the time comes, create the VAT rule on the discounted amount (for example code TVA18, 18%): it will apply to new documents without any software change.',
    prefill: 'Prefill this example',
  },
  count: '{{count}} rule',
  count_other: '{{count}} rules',
  filters: { active: 'Active', inactive: 'Inactive', all: 'All' },
  columns: {
    rule: 'Rule',
    code: 'Code',
    kind: 'Kind',
    rate: 'Rate',
    base: 'Base',
    appliesTo: 'Scope',
    validity: 'Validity',
    status: 'Status',
    actions: 'Actions',
  },
  kind: { TAX: 'Tax', WITHHOLDING: 'Withholding' },
  kindDescription: {
    TAX: 'Added to the amount due (e.g. VAT).',
    WITHHOLDING: 'Deducted from the amount due (e.g. 5% withholding tax).',
  },
  base: { SUBTOTAL: 'Gross subtotal', DISCOUNTED_SUBTOTAL: 'Discounted amount' },
  appliesTo: { ALL: 'All', ACCOMMODATION: 'Accommodation', SERVICES: 'Services' },
  flags: { inclusive: 'Included in prices', optional: 'Optional' },
  status: { active: 'Active', inactive: 'Inactive' },
  validity: {
    open: 'No limit',
    from: 'From {{date}}',
    until: 'Until {{date}}',
    between: 'From {{from}} to {{to}}',
  },
  actions: {
    edit: 'Edit',
    deactivate: 'Deactivate',
    activate: 'Reactivate',
  },
  confirm: {
    deactivateTitle: 'Deactivate {{name}}?',
    deactivateBody:
      'The rule will no longer apply to new documents. Proformas and invoices already issued remain unchanged.',
    deactivateConfirm: 'Deactivate rule',
    activateTitle: 'Reactivate {{name}}?',
    activateBody: 'The rule will apply again to new documents, according to its validity period.',
    activateConfirm: 'Reactivate rule',
  },
  drawer: {
    createTitle: 'New tax rule',
    editTitle: 'Edit {{name}}',
    kind: 'Kind',
    kindLocked: 'The kind of a rule cannot change after creation: issued documents refer to it.',
    code: 'Code',
    codeHint:
      '2 to 20 characters: letters, digits, hyphen or underscore. Upper-cased; cannot be changed after creation.',
    name: 'Label',
    nameHint: 'As printed on proformas and invoices.',
    rate: 'Rate (%)',
    rateHint: 'Enter 18 for 18%.',
    base: 'Base',
    baseHint: 'Amount the rate applies to.',
    appliesTo: 'Scope',
    appliesToHint: 'Lines concerned: all, accommodation only or services only.',
    isInclusive: 'Rate included in displayed prices',
    isInclusiveHint: 'Prices are deemed tax-inclusive: the tax is extracted from the amount.',
    isOptional: 'Optional, enabled document by document',
    isOptionalHint: 'An “Apply” checkbox is offered on each proforma or invoice.',
    validFrom: 'Valid from',
    validTo: 'Valid until',
    validityHint: 'Leave empty for unlimited validity.',
    sortOrder: 'Display order',
    sortOrderHint: '0 to 1000; rules are listed in ascending order.',
    submitCreate: 'Create rule',
    submitEdit: 'Save',
    reload: 'Reload rule',
  },
  validation: {
    code: '2 to 20 characters: letters, digits, hyphen or underscore.',
    name: 'Between 2 and 120 characters.',
    rate: 'Enter a rate between 0.01 and 100.',
    sortOrder: 'Whole number between 0 and 1000.',
    validity: 'The end of validity must be after its start.',
    codeExists: 'This code is already used by another rule.',
  },
  toasts: {
    created: 'Rule {{code}} created.',
    updated: 'Rule updated.',
    deactivated: 'Rule {{code}} deactivated.',
    activated: 'Rule {{code}} reactivated.',
  },
  emptyTitle: 'No tax rule',
  emptyBody: 'No tax or withholding is configured: documents are issued tax-free (CME regime).',
  emptyFilteredTitle: 'No rule for this filter',
  emptyFilteredBody: 'Change the filter to display the other rules.',
};

const pos = {
  title: 'Point of sale',
  subtitle: 'Order taking, kitchen tickets and payment.',
  noOutlet: {
    title: 'No outlet',
    body: 'Create an outlet (restaurant, bar…) for this property before opening the point of sale.',
    action: 'Outlets & printing',
  },
  bar: {
    outlet: 'Outlet',
    cashOpen: 'Cash open',
    cashClosed: 'Cash closed',
    server: 'Server',
    openOrders_one: '{{count}} open order',
    openOrders_other: '{{count}} open orders',
    newOrder: 'New order',
    happyHour: 'Happy hour',
    happyHourUntil: 'until {{time}}',
    happyHourAll: 'whole menu',
    tables: 'Tables',
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    exit: 'Leave the register',
    clock: 'Time',
    views: 'View',
    viewCatalog: 'Menu',
    viewTicket_one: 'Order · {{count}} item · {{total}}',
    viewTicket_other: 'Order · {{count}} items · {{total}}',
  },
  context: {
    title: 'Table and service',
    hint: 'Tap a free table to assign it, or choose the service type.',
    browseTitle: 'Tables',
    browseHint: 'Tap an occupied table to resume its order, a free table to open one.',
    apply: 'Apply',
    tables: 'Tables',
    noTable: 'No table',
    noTables: 'No table for this outlet.',
    current: 'Table of this order',
    coversMore: 'One more cover',
    coversLess: 'One cover less',
  },
  serviceType: {
    DINE_IN: 'Dine in',
    COUNTER: 'Counter',
    TAKEAWAY: 'Takeaway',
    DELIVERY: 'Delivery',
    ROOM_SERVICE: 'Room service',
  },
  status: { OPEN: 'Open', PAID: 'Paid', TRANSFERRED: 'Charged to stay', CANCELLED: 'Cancelled' },
  preparation: { NEW: 'New', IN_PREPARATION: 'In preparation', READY: 'Ready', SERVED: 'Served' },
  itemStatus: {
    PENDING: 'To send',
    SENT: 'In preparation',
    READY: 'Ready',
    SERVED: 'Served',
    CANCELLED: 'Cancelled',
  },
  newOrder: {
    title: 'New order',
    description: 'Pick a free table or a service without table.',
    descriptionBar: 'Open a tab at the counter or pick a table.',
    table: 'Table',
    noTable: 'No table',
    occupied: 'occupied',
    covers: 'Covers',
    serviceType: 'Service type',
    tabName: 'Tab name',
    tabNameHint: 'Guest first name, “blue shirt”, token number… unique among open tabs.',
    notes: 'Note',
    create: 'Open order',
  },
  categories: {
    all: 'All',
    search: 'Search a product…',
    empty: 'No product in this category.',
    clear: 'Clear',
  },
  product: {
    unavailable: 'Out of stock',
    options: 'Options',
    happyHour: 'Happy hour',
    add: 'Add {{name}}',
    increment: 'Add one',
    decrement: 'Remove one',
  },
  ticket: {
    title: 'Bill',
    order: 'Order',
    empty: 'No order selected',
    emptyHint: 'Tap a dish to start the order.',
    next: 'New order',
    nextHint: 'Created with the first dish',
    lines: 'Order lines',
    assignTable: 'Assign a table',
    customer: 'Customer',
    receiptShort: 'Receipt',
    more: 'More',
    payAction: 'Payment',
    paySendsFirst_one: 'sends {{count}} line to the kitchen first',
    paySendsFirst_other: 'sends {{count}} lines to the kitchen first',
    noLines: 'Add products from the menu.',
    pending: 'To send',
    sent: 'Sent to kitchen',
    subtotal: 'Subtotal',
    discount: 'Discount',
    tax: 'Taxes',
    total: 'Total',
    paid: 'Paid',
    balance: 'Balance due',
    send: 'Send to kitchen',
    sendCount: 'Send to kitchen ({{count}})',
    requestBill: 'Request bill',
    billRequested: 'Bill requested',
    discountAction: 'Discount',
    cancelOrder: 'Cancel order',
    cancelItem: 'Cancel line',
    remove: 'Remove',
    receipt: 'Receipt',
    printReceipt: 'Print receipt',
    covers_one: '{{count}} cover',
    covers_other: '{{count}} covers',
    payWith: 'Pay',
    pay: {
      CASH: 'Cash',
      MOBILE_MONEY: 'Mobile money',
      CARD: 'Card',
      BANK_TRANSFER: 'Bank transfer',
      CHEQUE: 'Cheque',
      OTHER: 'Other',
    },
    closed: 'Order paid',
    cancelled: 'Order cancelled',
    markServed: 'Served',
    transfer: 'Charge to room',
    transferredTo: 'Charged to stay',
    transferSlip: 'Transfer slip',
    tab: 'Tab {{name}}',
    listPrice: 'list price {{price}}',
    repeatRound: 'Repeat the round',
  },
  tabs: {
    title: 'Counter',
    subtitle: 'Open tabs of the outlet: resume at the point of sale in one tap.',
    empty: 'No open tab',
    emptyHint: 'Open a tab at the counter from the point of sale.',
    newTab: 'New tab',
    resume: 'Resume',
    items_one: '{{count}} item',
    items_other: '{{count}} items',
    since: 'since {{time}}',
    noName: 'Unnamed',
  },
  transfer: {
    title: 'Charge order #{{number}} to a stay',
    description:
      'The {{total}} total will be posted to the room account. The transfer slip is printed for the guest to sign.',
    search: 'In-house stay',
    searchPlaceholder: 'Room number, guest name, reference…',
    stays: 'In-house stays',
    emptyTitle: 'No in-house stay',
    emptyBody: 'Only checked-in reservations of this property can receive a bill.',
    confirm: 'Charge to {{room}}',
    confirmEmpty: 'Pick a stay',
    folioSoFar: '{{amount}} already on the folio',
    done: 'Bill charged to stay {{reference}} — have the transfer slip signed.',
    blockedByPayments: 'Payments are recorded: a partially paid bill cannot be charged to a room.',
  },
  optionsDialog: {
    title: 'Options — {{name}}',
    required: 'required',
    chooseOne: 'Choose one option',
    chooseUpTo: 'Up to {{count}} options',
    quantity: 'Quantity',
    notes: 'Instructions',
    notesPlaceholder: 'No onions, well done…',
    add: 'Add to order',
  },
  payment: {
    title: 'Payment',
    back: 'Back to the order',
    summary: 'Order to collect',
    method: 'Payment method',
    due: 'Total due',
    received: 'Amount received',
    remainingAfter: 'Remaining after this payment: {{amount}}',
    exceeds: 'The amount exceeds the balance due.',
    quick: 'Quick amounts',
    exact: 'Exact amount',
    payments: 'Payments',
    room: 'Room',
    keypad: 'Keypad',
    backspace: 'Delete last digit',
    clear: 'Clear',
    validate: 'Confirm',
    validatePartial: 'Collect part',
    printReceipt: 'Print receipt',
    transferredTitle: 'Bill charged to the stay',
    amount: 'Amount collected',
    remaining: 'Balance due: {{amount}}',
    provider: 'Provider',
    tendered: 'Cash tendered',
    change: 'Change due',
    reference: 'Transaction reference (optional)',
    notes: 'Note',
    confirm: 'Confirm payment',
    cashClosed: 'Open a cash session to take cash.',
    openCash: 'Open cash',
    paidTitle: 'Order paid',
    paidBody: 'Total {{total}} · change {{change}}',
    paidBodyNoChange: 'Total {{total}}',
    partial: 'Partial payment recorded: {{balance}} remaining.',
  },
  reason: {
    cancelOrderTitle: 'Cancel order',
    cancelOrderBody:
      'Sent lines will produce a cancellation ticket in the kitchen. This cannot be undone.',
    cancelItemTitle: 'Cancel line “{{label}}”',
    cancelItemBody: 'A cancellation ticket goes to the kitchen.',
    discountTitle: 'Order discount',
    label: 'Reason',
    placeholder: 'Reason (at least 5 characters)',
    confirm: 'Confirm',
    discountType: 'Discount type',
    percent: 'Percentage',
    amount: 'Amount',
    none: 'No discount',
    value: 'Value',
    removeDiscount: 'Remove discount',
  },
  kitchen: {
    title: 'Kitchen',
    subtitle: 'Tickets in preparation, oldest first.',
    empty: 'Nothing in preparation',
    emptyHint: 'Tickets appear here as soon as an order is sent.',
    ready: 'Ready',
    served: 'Served',
    reprint: 'Reprint',
    reprinted: 'Ticket reprinted.',
    destination: 'Destination',
    allDestinations: 'All destinations',
    age: '{{count}} min',
    late: 'Late',
    cancelTicket: 'Cancellation',
    showText: 'Show ticket',
    print: 'Print',
    table: 'Table {{code}}',
  },
  tables: {
    title: 'Floor plan',
    subtitle: 'Table status and open orders.',
    legend: { FREE: 'Free', OCCUPIED: 'Occupied', BILL_REQUESTED: 'Bill requested' },
    edit: 'Edit plan',
    done: 'Done',
    newArea: 'New area',
    newTable: 'New table',
    editTable: 'Edit table',
    editArea: 'Edit area',
    area: 'Area',
    noArea: 'No area',
    seats_one: '{{count}} seat',
    seats_other: '{{count}} seats',
    shape: { SQUARE: 'Square', ROUND: 'Round', RECT: 'Rectangle' },
    code: 'Number',
    name: 'Name',
    seats: 'Seats',
    shapeLabel: 'Shape',
    active: 'Active',
    open: 'Open an order',
    resume: 'Resume order',
    empty: 'No table',
    emptyHint: 'Create areas and tables to follow the service.',
    since: 'since {{time}}',
    toasts: { tableSaved: 'Table saved.', areaSaved: 'Area saved.' },
  },
  catalog: {
    title: 'Menu',
    subtitle: 'Categories, products, prices and options.',
    newCategory: 'New category',
    newProduct: 'New product',
    editCategory: 'Edit category',
    editProduct: 'Edit product',
    allCategories: 'All categories',
    search: 'Search…',
    empty: 'No product',
    emptyHint: 'Add a first product to this category.',
    showInactive: 'Show inactive items',
    unavailable: 'Out of stock',
    inactive: 'Inactive',
    options_one: '{{count}} option group',
    options_other: '{{count}} option groups',
    fields: {
      code: 'Code',
      name: 'Name',
      description: 'Description',
      price: 'Price',
      unit: 'Unit',
      taxRules: 'Tax rules',
      taxRulesHint: 'Comma-separated codes (e.g. TVA18). Empty = no tax.',
      destination: 'Print destination',
      destinationInherit: 'Same as category',
      destinationNone: 'Outlet default',
      outlet: 'Restricted to outlet',
      outletAll: 'All outlets',
      icon: 'Icon',
      color: 'Colour',
      sortOrder: 'Order',
      available: 'Available for sale',
      active: 'Active',
      category: 'Category',
      optionGroups: 'Option groups',
      groupName: 'Group name',
      groupKind: 'Choice',
      SINGLE: 'Single',
      MULTI: 'Multiple',
      minChoices: 'Minimum',
      maxChoices: 'Maximum',
      unlimited: 'Unlimited',
      optionName: 'Option',
      priceDelta: 'Extra',
      addGroup: 'Add group',
      addOption: 'Add option',
      removeGroup: 'Remove group',
      removeOption: 'Remove',
    },
    colors: {
      GREEN: 'Green',
      GOLD: 'Gold',
      SLATE: 'Slate',
      OLIVE: 'Olive',
      TERRACOTTA: 'Terracotta',
    },
    toasts: { categorySaved: 'Category saved.', productSaved: 'Product saved.' },
  },
  settings: {
    title: 'Outlets & printing',
    subtitle: 'Property outlets and print destinations (kitchen, bar, cashier).',
    outlets: 'Outlets',
    destinations: 'Print destinations',
    newOutlet: 'New outlet',
    newDestination: 'New destination',
    editOutlet: 'Edit outlet',
    editDestination: 'Edit destination',
    test: 'Test',
    tested: 'Test ticket printed.',
    testedBrowser: 'No network printer on this destination: test ticket generated.',
    default: 'Default',
    inactive: 'Inactive',
    emptyOutlets: 'No outlet',
    emptyOutletsHint: 'An outlet carries its menu, tables and printers.',
    emptyDestinations: 'No destination',
    emptyDestinationsHint: 'Without a destination, tickets stay visible on the kitchen screen.',
    fields: {
      code: 'Code',
      name: 'Name',
      kind: 'Kind',
      driver: 'Driver',
      host: 'Printer IP address',
      port: 'Port',
      width: 'Paper width',
      copies: 'Copies',
      isDefault: 'Default destination of the outlet',
      active: 'Active',
      paymentMethods: 'Payment methods accepted at the register',
      paymentMethodsHint:
        'Offered at the “Payment” step, in this order. An unchecked method is refused for this outlet.',
    },
    outletKind: {
      RESTAURANT: 'Restaurant',
      BAR: 'Bar',
      ROOM_SERVICE: 'Room service',
      SHOP: 'Shop',
      OTHER: 'Other',
    },
    destinationKind: {
      KITCHEN: 'Kitchen',
      BAR: 'Bar',
      GRILL: 'Grill',
      PASTRY: 'Pastry',
      CASHIER: 'Cashier',
      OTHER: 'Other',
    },
    driver: {
      BROWSER: 'Browser (print from this device)',
      ESCPOS_NETWORK: 'ESC/POS network printer',
      NONE: 'Screen only',
    },
    happyHours: 'Happy hours',
    newHappyHour: 'New happy hour',
    editHappyHour: 'Edit happy hour',
    emptyHappyHours: 'No happy hour',
    emptyHappyHoursHint:
      'A happy hour lowers the price of part of the menu on a recurring time slot; the reduced price is frozen when the line is added.',
    happyHourFields: {
      name: 'Name',
      days: 'Days',
      start: 'Start',
      end: 'End',
      overnight: 'An end earlier than the start crosses midnight.',
      discountType: 'Discount',
      discountValue: 'Value',
      categories: 'Categories',
      allCategories: 'Whole menu',
      active: 'Active',
      schedule: 'Slot',
      scope: 'Scope',
    },
    days: { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' },
    toasts: {
      outletSaved: 'Outlet saved.',
      destinationSaved: 'Destination saved.',
      happyHourSaved: 'Happy hour saved.',
    },
  },
  orders: {
    title: 'Orders',
    subtitle: 'History and tracking of outlet orders.',
    stayShort: 'Stay {{reference}}',
    search: 'Reference, number or tab…',
    allStatuses: 'All statuses',
    columns: {
      number: 'No.',
      reference: 'Reference',
      table: 'Table',
      status: 'Status',
      preparation: 'Preparation',
      items: 'Lines',
      total: 'Total',
      balance: 'Balance',
      server: 'Server',
      openedAt: 'Opened',
    },
    empty: 'No order',
    emptyHint: 'Orders show up here after the first order is taken.',
    kpi: {
      revenue: 'Revenue collected',
      paid: 'Paid orders',
      average: 'Average ticket',
      open: 'In progress',
    },
    topProducts: 'Top products',
    byMethod: 'By payment method',
    detail: {
      lines: 'Lines',
      payments: 'Payments',
      tickets: 'Kitchen tickets',
      taxes: 'Taxes',
      noPayments: 'No payment',
      noTickets: 'No ticket',
      openInPos: 'Open in point of sale',
      stay: 'Stay {{reference}}',
      discountReason: 'Discount reason',
      cancellationReason: 'Cancellation reason',
    },
  },
  toasts: {
    orderCreated: 'Order #{{number}} opened.',
    roundRepeated: 'Round repeated: lines to send.',
    sent: 'Sent to kitchen.',
    cancelled: 'Order cancelled.',
    itemCancelled: 'Line cancelled.',
    discountApplied: 'Discount applied.',
    billRequested: 'Bill requested.',
    ticketReady: 'Ticket ready.',
    ticketServed: 'Ticket served.',
    receiptPrinted: 'Receipt printed.',
    slipPrinted: 'Transfer slip printed.',
    slipBrowser: 'No cashier printer: transfer slip opened in the browser.',
    receiptBrowser: 'No network cashier printer: receipt opened in the browser.',
  },
};

export const stock = {
  title: 'Stock',
  nav: {
    levels: 'Levels & alerts',
    movements: 'Movements',
    purchases: 'Purchases',
    counts: 'Stock counts',
    items: 'Items & locations',
  },
  noProperty: { title: 'No property', body: 'Select a property to view its stock.' },
  category: { BEVERAGE: 'Beverages', FOOD: 'Food', SUPPLY: 'Supplies', OTHER: 'Other' },
  unit: {
    PIECE: 'piece',
    BOTTLE: 'bottle',
    CAN: 'can',
    L: 'l',
    CL: 'cl',
    KG: 'kg',
    G: 'g',
    PORTION: 'portion',
  },
  movementType: {
    PURCHASE: 'Purchase',
    SALE: 'Sale',
    SALE_REVERSAL: 'Sale reversal',
    ADJUSTMENT: 'Adjustment',
    LOSS: 'Loss',
    TRANSFER_OUT: 'Transfer (out)',
    TRANSFER_IN: 'Transfer (in)',
    COUNT: 'Stock count',
  },
  levels: {
    title: 'Levels & alerts',
    subtitle: 'Quantities per location, alert thresholds and stock value at average cost.',
    kpi: { items: 'Tracked items', alerts: 'Alerts', value: 'Stock value', locations: 'Locations' },
    search: 'Search an item',
    allCategories: 'All categories',
    allLocations: 'All locations',
    alertsOnly: 'Alerts only',
    columns: {
      item: 'Item',
      locations: 'Per location',
      total: 'Total',
      min: 'Threshold',
      value: 'Value',
      actions: 'Actions',
    },
    empty: 'No item',
    emptyHint: 'Create your items and locations under “Items & locations”, then record an entry.',
    noAlert: 'No alert: every item is above its threshold.',
    alert: 'Alert',
    negative: 'Negative',
    adjust: 'Adjust / loss',
    transfer: 'Transfer',
  },
  movement: {
    title: 'Stock movement',
    transferTitle: 'Transfer between locations',
    kind: 'Kind',
    adjustment: 'Adjustment (signed)',
    loss: 'Loss (breakage, expired)',
    item: 'Item',
    location: 'Location',
    from: 'From',
    to: 'To',
    quantity: 'Quantity',
    quantityHint: 'In {{unit}}; negative for an outflow, positive for an inflow.',
    lossHint: 'Quantity removed, in {{unit}}.',
    unitCost: 'Unit cost (inflow)',
    unitCostHint: 'Optional: updates the weighted average cost.',
    reason: 'Reason',
    reasonHint: 'Required: it appears in the journal (R-INV-08).',
    submit: 'Record movement',
    submitTransfer: 'Transfer',
    saved: 'Movement recorded.',
    transferred: 'Transfer recorded.',
  },
  movements: {
    title: 'Movements',
    subtitle: 'Immutable journal: every quantity change with its author, reason and source.',
    allTypes: 'All types',
    columns: {
      date: 'Date',
      type: 'Type',
      item: 'Item',
      location: 'Location',
      quantity: 'Quantity',
      after: 'After',
      reason: 'Reason / source',
      author: 'Author',
    },
    empty: 'No movement',
    emptyHint: 'Inflows, sales, losses, transfers and counts will appear here.',
  },
  items: {
    title: 'Items & locations',
    subtitle: 'Tracked items (stock unit, purchase packaging, threshold) and storage locations.',
    itemsCard: 'Items',
    locationsCard: 'Storage locations',
    newItem: 'New item',
    editItem: 'Edit item',
    newLocation: 'New location',
    editLocation: 'Edit location',
    showInactive: 'Show inactive',
    columns: {
      code: 'Code',
      name: 'Name',
      category: 'Category',
      unit: 'Unit',
      package: 'Packaging',
      min: 'Threshold',
      cost: 'Average cost',
      outlet: 'Outlet',
      default: 'Default',
      status: 'Status',
    },
    active: 'Active',
    inactive: 'Inactive',
    isDefault: 'Default location',
    emptyItems: 'No item',
    emptyItemsHint: 'An item is what you count: a bottle, a kilo, a portion.',
    emptyLocations: 'No location',
    emptyLocationsHint:
      'Storeroom, bar, kitchen… The first location created becomes the default one.',
    fields: {
      code: 'Code',
      name: 'Name',
      category: 'Category',
      unit: 'Stock unit',
      packageLabel: 'Purchase packaging',
      packageLabelHint: 'E.g. “Crate of 24”, “70 cl bottle”.',
      packageQuantity: 'Units per package',
      minQuantity: 'Alert threshold',
      minQuantityHint: 'In stock unit; empty = no threshold alert.',
      outlet: 'Outlet served from this location',
      noOutlet: 'None (storeroom, kitchen…)',
      isDefault: 'Default location (purchase receipts)',
      isActive: 'Active',
    },
    toasts: { itemSaved: 'Item saved.', locationSaved: 'Location saved.' },
  },
  recipe: {
    title: 'Stock recipe',
    description:
      'What one sold unit consumes, in each item’s stock unit. Without a recipe, the product does not touch stock.',
    button: 'Recipe',
    empty: 'No item: this product does not consume stock.',
    addLine: 'Add an item',
    item: 'Item',
    quantity: 'Quantity per sold unit',
    saved: 'Recipe saved.',
    lines: '{{count}} stock item(s)',
  },
  purchases: {
    title: 'Purchases',
    subtitle:
      'Purchase orders in packages; receiving brings stock in and updates the average cost.',
    new: 'New purchase order',
    allStatuses: 'All statuses',
    status: { DRAFT: 'Draft', RECEIVED: 'Received', CANCELLED: 'Cancelled' },
    columns: {
      reference: 'Reference',
      supplier: 'Supplier',
      location: 'Location',
      date: 'Date',
      lines: 'Lines',
      total: 'Total',
      status: 'Status',
    },
    receive: 'Receive',
    cancel: 'Cancel order',
    cancelTitle: 'Cancel this purchase order?',
    cancelDescription: 'The draft will be cancelled without any stock movement.',
    empty: 'No purchase order',
    emptyHint: 'Enter an order in packages, then receive it on delivery.',
    drawer: {
      supplier: 'Supplier',
      supplierReference: 'Supplier reference',
      location: 'Receiving location',
      orderedAt: 'Date',
      notes: 'Notes',
      lines: 'Lines',
      item: 'Item',
      packages: 'Packages',
      packageCost: 'Package cost',
      addLine: 'Add a line',
      units: '{{count}} {{unit}}',
      total: 'Total',
    },
    toasts: {
      created: 'Purchase order created.',
      received: 'Purchase order received: stock is up to date.',
      cancelled: 'Purchase order cancelled.',
    },
    lineSummary: '{{count}} line(s)',
  },
  counts: {
    title: 'Stock counts',
    subtitle:
      'Physical count per location: expected quantities frozen at opening, variances posted as movements.',
    new: 'Open a stock count',
    location: 'Location to count',
    category: 'Limit to a category',
    allCategories: 'All items',
    notes: 'Notes',
    status: { OPEN: 'In progress', VALIDATED: 'Validated', CANCELLED: 'Cancelled' },
    columns: {
      reference: 'Reference',
      location: 'Location',
      started: 'Opened on',
      progress: 'Counted',
      status: 'Status',
    },
    detail: 'Count entry',
    expected: 'Expected',
    counted: 'Counted',
    variance: 'Variance',
    save: 'Save entry',
    validate: 'Validate count',
    validateTitle: 'Validate this stock count?',
    validateDescription:
      'Every non-zero variance becomes a count movement; uncounted lines are ignored. The count will be closed.',
    cancel: 'Cancel count',
    cancelTitle: 'Cancel this stock count?',
    cancelDescription: 'No movement will be created.',
    open: 'Open',
    empty: 'No stock count',
    emptyHint: 'Open a count on a location: expected quantities are frozen at that moment.',
    toasts: {
      opened: 'Stock count opened.',
      saved: 'Entry saved.',
      validated: 'Stock count validated: variances recorded.',
      cancelled: 'Stock count cancelled.',
    },
    progress: '{{counted}} / {{total}}',
  },
};

/** Platform console (phase H6, ADR-021): tenants, subscriptions, modules, Owner. */
export const platform = {
  title: 'Platform',
  locales: { fr: 'French', en: 'English' },
  activities: { HOTEL: 'Hotel', RESTAURANT: 'Restaurant', BAR: 'Bar', OTHER: 'Other' },
  status: { TRIAL: 'Trial', ACTIVE: 'Active', SUSPENDED: 'Suspended' },
  subscriptionStatus: {
    TRIAL: 'Trial',
    ACTIVE: 'Active',
    PAST_DUE: 'Past due (grace)',
    SUSPENDED: 'Suspended',
    CANCELLED: 'Cancelled',
  },
  moduleStatus: { AVAILABLE: 'Available', COMING_SOON: 'Coming soon' },
  mfa: {
    title: 'Two-factor authentication is off',
    body: 'The platform console requires a second factor in production.',
    link: 'Enable two-factor authentication',
  },
  overview: {
    title: 'Overview',
    subtitle: 'Tenants, subscriptions, module adoption and trials about to end.',
    kpi: { total: 'Tenants' },
    plans: 'Subscriptions by plan',
    modules: 'Module adoption',
    trials: 'Trials ending soon (14 days)',
    recent: 'Recently created tenants',
    noSubscription: 'No live subscription.',
    noTrial: 'No trial ends within fourteen days.',
    trialExpired: 'Expired',
    trialDays_one: '{{count}} day',
    trialDays_other: '{{count}} days',
    columns: {
      plan: 'Plan',
      tenants: 'Tenants',
      module: 'Module',
      availability: 'Availability',
      trialEnds: 'Trial ends',
    },
  },
  tenants: {
    title: 'Tenants',
    subtitle: 'Platform customers: record, subscription, modules and accounts.',
    new: 'New tenant',
    search: 'Search a tenant',
    allPlans: 'All plans',
    empty: 'No tenant',
    emptyHint: 'Provision your first customer with "New tenant".',
    neverLoggedIn: 'Never signed in',
    columns: {
      tenant: 'Tenant',
      status: 'Status',
      plan: 'Plan',
      modules: 'Modules',
      properties: 'Properties',
      users: 'Accounts',
      lastLogin: 'Last sign-in',
      created: 'Created',
    },
  },
  form: {
    title: 'New tenant',
    description:
      'Record, first property, subscription and Owner account are created together; the Owner receives an activation link.',
    submit: 'Provision',
    sections: {
      identity: 'Organisation',
      subscription: 'Subscription',
      property: 'First property',
      owner: 'Owner account',
    },
    legalName: 'Legal name',
    tradeName: 'Trade name',
    slug: 'Identifier',
    slugHint: 'Lowercase letters, digits and dashes; immutable once created.',
    countryCode: 'Country (ISO)',
    currencyCode: 'Currency (ISO)',
    timezone: 'Time zone',
    defaultLocale: 'Language',
    plan: 'Plan',
    status: 'Status',
    planModules: 'Plan modules: {{modules}}',
    trialEndsAt: 'Trial ends',
    trialHint: 'Informative off-platform; when invoiced, the first invoice is issued that day.',
    addons: 'Add-ons outside the plan',
    addonRequires: 'Requires: {{modules}}',
    notes: 'Internal notes',
    propertyCode: 'Code',
    propertyName: 'Property name',
    activities: 'Activities',
    activitiesRequired: 'At least one activity.',
    ownerFullName: 'Full name',
    ownerEmail: 'E-mail',
    ownerLocale: 'Owner language',
    ownerNotice:
      'The address must be unique across the platform. The activation link is shown to you on screen, in addition to the e-mail.',
  },
  detail: {
    identity: 'Record',
    editTitle: 'Edit record',
    slugImmutable: 'The identifier never changes.',
    properties: 'Properties',
    noProperty: 'No property',
    inactive: 'Inactive',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    suspendedNotice: 'Tenant suspended: its users can no longer sign in. No data is deleted.',
  },
  subscription: {
    title: 'Subscription',
    edit: 'Edit',
    editTitle: 'Subscription',
    editDescription: 'Plan, trial or active status, end date and internal notes.',
    none: 'No live subscription.',
    since: 'Since {{date}}',
    trialEnds: 'trial ends {{date}}',
    ends: 'ends {{date}}',
    startsAt: 'Start',
    history: 'Subscription history',
    planChangeNotice:
      'Changing the plan recomputes the plan modules; add-ons stay enabled. An active subscription has no trial end date.',
    billingSection: 'Billing by HOREVIA',
    billingMode: 'Billing mode',
    billingPeriod: 'Billing period',
    priceAmount: 'Price per period ({{currency}})',
    priceSuggested: 'Catalogue: {{amount}} (plan {{plan}}{{addons}}).',
    priceNoCatalog: 'No catalogue price in this currency: enter the negotiated price.',
    autoSuspend: 'Automatic suspension after notice when unpaid',
    limits: 'Limits (empty = plan limits, 0 = unlimited)',
    limitProperties: 'Properties',
    limitResources: 'Resources',
    limitUsers: 'Accounts',
    endsAt: 'Subscription end (scheduled termination)',
    invoicedNotice:
      'An active invoiced subscription is invoiced right away, then every period; on trial, when the trial ends. Past due after {{grace}} days, automatic suspension on a business day after notice.',
  },
  modules: {
    title: 'Modules',
    plan: 'Included in the plan',
    addon: 'Add-on',
    inPlanDisabled: 'Included, disabled',
    off: 'Not enabled',
    toggle: 'Enable or disable {{module}}',
  },
  owner: {
    title: 'Owner accounts',
    invite: 'Invite',
    inviteTitle: 'Invite an Owner',
    inviteDescription:
      'New link for an account still invited, or a new Owner account for this tenant.',
    inviteSubmit: 'Send invitation',
    inviteNotice: 'The activation link is shown to you on screen, in addition to the e-mail.',
    none: 'No Owner account',
    invitationPending: 'Invitation pending',
    mfa: 'MFA',
    columns: { account: 'Account', status: 'Status', lastLogin: 'Last sign-in' },
  },
  suspend: {
    title: 'Suspend {{name}}',
    description:
      'Sessions are revoked and sign-in is refused until reactivation. The reason is logged, for the tenant too.',
    reason: 'Reason',
    confirm: 'Suspend tenant',
  },
  billing: {
    title: 'Billing',
    subtitle:
      'Subscriptions invoiced by HOREVIA: recurring revenue, invoices, payments and overdue.',
    kpi: {
      mrr: 'Monthly recurring revenue',
      outstanding: 'Outstanding',
      overdue: 'Overdue',
      paid30: 'Paid in the last 30 days',
    },
    kpiInvoices_one: '{{count}} invoice',
    kpiInvoices_other: '{{count}} invoices',
    kpiSubscriptions_one: '{{count}} invoiced subscription',
    kpiSubscriptions_other: '{{count}} invoiced subscriptions',
    noMoney: '—',
    run: 'Run the cycle',
    runDone:
      'Cycle run: {{activated}} activation(s), {{invoiced}} invoice(s), {{pastDue}} past due, {{suspended}} suspension(s).',
    invoices: 'Invoices',
    none: 'No subscription invoice.',
    noneHint:
      'Switch a subscription to “Invoiced by HOREVIA” from the tenant page: the first invoice is issued right away.',
    allStatuses: 'All statuses',
    overdueOnly: 'Overdue only',
    columns: {
      number: 'Number',
      tenant: 'Tenant',
      period: 'Period',
      issued: 'Issued',
      due: 'Due',
      total: 'Total',
      balance: 'Balance',
      status: 'Status',
      actions: 'Actions',
    },
    status: {
      ISSUED: 'Issued',
      PARTIALLY_PAID: 'Partially paid',
      PAID: 'Paid',
      CANCELLED: 'Cancelled',
    },
    overdue: 'Overdue',
    overdueTenants: 'Tenants with overdue invoices',
    noOverdue: 'No overdue invoice.',
    upcoming: 'Upcoming invoices (30 days)',
    noUpcoming: 'Nothing to issue within thirty days.',
    upcomingKind: { RENEWAL: 'Renewal', TRIAL_END: 'Trial end' },
    suspensionOn: 'Suspension on {{date}}',
    oldestDue: 'Overdue since {{date}}',
    issueNext: 'Issue the next invoice',
    download: 'PDF',
    pay: 'Record payment',
    cancel: 'Cancel',
    reverse: 'Reverse this payment',
    payments: 'Payments',
    reversed: 'reversed',
    mode: { MANUAL: 'Off-platform', INVOICED: 'Invoiced by HOREVIA' },
    period: { MONTHLY: 'Monthly', YEARLY: 'Yearly' },
    perPeriod: { MONTHLY: '/ month', YEARLY: '/ year' },
    payment: {
      title: 'Record a payment',
      description: 'Invoice {{number}} · remaining balance {{balance}}.',
      amount: 'Amount',
      method: 'Payment method',
      provider: 'Provider or bank',
      reference: 'Reference',
      paidAt: 'Payment date',
      notes: 'Note',
      confirm: 'Record',
      methods: {
        BANK_TRANSFER: 'Bank transfer',
        MOBILE_MONEY: 'Mobile money',
        CASH: 'Cash',
        CHEQUE: 'Cheque',
        OTHER: 'Other',
      },
    },
    cancelInvoice: {
      title: 'Cancel invoice {{number}}',
      description:
        'Only an issued invoice without any payment can be cancelled. The number stays consumed; the period can be invoiced again.',
      reason: 'Reason',
      confirm: 'Cancel the invoice',
    },
    reversePayment: {
      title: 'Reverse a payment',
      description:
        'The {{amount}} payment will be marked reversed and the invoice balance recomputed.',
      reason: 'Reason',
      confirm: 'Reverse the payment',
    },
    card: {
      title: 'Billing',
      mode: 'Mode',
      price: 'Price',
      currentPeriod: 'Current period',
      nextInvoice: 'Next invoice',
      autoSuspend: 'Automatic suspension',
      on: 'Yes',
      off: 'No',
      pastDue: 'Past due since {{date}}',
      suspension: 'Suspension scheduled on {{date}}',
      limits: 'Limits',
      unlimited: 'unlimited',
      manualHint:
        'Off-platform subscription: no invoice and no automatic suspension. Edit the subscription to invoice it.',
      trialHint: 'The first invoice will be issued when the trial ends.',
    },
    toasts: {
      paymentRecorded: 'Payment recorded.',
      invoiceCancelled: 'Invoice cancelled.',
      paymentReversed: 'Payment reversed.',
      issued: 'Invoice issued.',
    },
  },
  pricing: {
    title: 'Pricing and limits',
    subtitle:
      'Catalogue prices per plan and add-on (proposed at subscription time, never applied unless copied onto the subscription) and limits per plan.',
    currency: 'Currency',
    plans: 'Plans',
    modules: 'Add-ons',
    columns: {
      plan: 'Plan',
      module: 'Add-on',
      monthly: 'Monthly',
      yearly: 'Yearly',
      properties: 'Properties',
      resources: 'Resources',
      users: 'Accounts',
    },
    limitHint: 'Empty = unlimited.',
    priceHint: 'Empty = not offered in this currency.',
    save: 'Save pricing',
    toasts: { saved: 'Pricing and limits saved.' },
  },
  toasts: {
    created: 'Tenant "{{name}}" provisioned.',
    updated: 'Record updated.',
    subscriptionUpdated: 'Subscription updated.',
    moduleEnabled: 'Module enabled for the tenant.',
    moduleDisabled: 'Module disabled for the tenant.',
    suspended: 'Tenant suspended.',
    reactivated: 'Tenant reactivated.',
    ownerInvited: 'Invitation sent.',
    ownerInvitedNoEmail: 'Invitation created: hand the link over yourself.',
  },
};

export const support = {
  title: 'Support access',
  subtitle:
    'Requests from HOREVIA operators to access your data read-only, to assist you. You decide, you can revoke at any time, and every step is written to your audit log.',
  state: {
    PENDING: 'Pending',
    APPROVED: 'Approved',
    EXPIRED: 'Expired',
    ENDED: 'Ended',
    DECLINED: 'Declined',
    CANCELLED: 'Withdrawn',
    REVOKED: 'Revoked',
  },
  columns: {
    tenant: 'Tenant',
    operator: 'Operator',
    request: 'Request',
    reason: 'Reason',
    state: 'State',
    decision: 'Decision',
    actions: 'Actions',
  },
  requestedBy: 'Requested by {{name}}',
  requestedDuration: 'Requested duration: {{duration}}',
  approvedDuration: 'Granted duration: {{duration}}',
  decidedBy: 'Decided by {{name}}',
  until: 'until {{date}}',
  requestsServed_one: '{{count}} request served',
  requestsServed_other: '{{count}} requests served',
  note: 'Note (optional, visible to the other party)',
  listTitle: 'Requests received',
  none: 'No support access request.',
  noneTenant:
    'No platform operator has asked to access your data. You will be notified by e-mail and in the app.',
  card: {
    title: 'Support access',
    hint: 'Access is requested here; the Owner approves it from their application.',
    request: 'Request access',
    enter: 'Enter',
    cancel: 'Withdraw',
    mine: 'My support accesses',
  },
  request: {
    title: 'Request support access',
    description:
      'The Owner of {{name}} will receive an e-mail and a notification; they can approve, shorten the duration or decline.',
    reason: 'Reason (visible to the tenant)',
    minutes: 'Requested duration in minutes (at most {{max}})',
    submit: 'Send request',
    notice:
      'Access will be read-only: no change, no export, no identity document. Every step is logged at the tenant and on the platform.',
  },
  approve: {
    action: 'Approve',
    title: 'Approve support access',
    description:
      '{{name}} will be able to read your data for the granted duration. You can revoke at any time.',
    minutes: 'Granted duration in minutes (at most {{max}})',
    confirm: 'Approve',
  },
  decline: {
    action: 'Decline',
    title: 'Decline the request',
    description: '{{name}} will be informed; no access will be opened.',
    confirm: 'Decline',
  },
  revoke: {
    action: 'Revoke',
    title: 'Revoke access',
    description: '{{name}} loses access on their next action.',
    confirm: 'Revoke',
  },
  banner: {
    title: 'Support access at {{name}} — read only',
    body: 'Until {{date}}. Reason: {{reason}}',
    leave: 'Leave',
    end: 'End access',
    ended: 'Support access has ended.',
  },
  toasts: {
    requested: 'Request sent to the Owner.',
    cancelled: 'Request withdrawn.',
    approved: 'Access approved.',
    declined: 'Request declined.',
    revoked: 'Access revoked.',
    entered: 'You are at {{name}}, read only.',
    left: 'Back to the platform console.',
  },
};

export const onboarding = {
  title: 'Getting started',
  subtitle:
    'What is left to set up before running HOREVIA day to day. Computed from your real data, adapted to your modules; nothing blocks the application.',
  progress: '{{done}} of {{total}} steps',
  count_one: '{{count}} item',
  count_other: '{{count}} items',
  complete: 'Everything is set up. The assistant no longer shows on the dashboard.',
  hiddenNotice: 'The assistant is hidden: it no longer shows on the dashboard.',
  dismiss: 'Hide assistant',
  restore: 'Show assistant again',
  status: { DONE: 'Done', TODO: 'To do', SKIPPED: 'Skipped' },
  actions: { configure: 'Set up', skip: 'Skip', unskip: 'Restore' },
  groups: { identity: 'Identity', property: 'Property', sales: 'Sales', team: 'Team' },
  steps: {
    identity: {
      title: 'Contact details',
      description: 'E-mail or phone shown on your documents and e-mails.',
    },
    legal: {
      title: 'Legal details',
      description: 'Trade register or tax id, tax regime, legal form, activity.',
    },
    branding: { title: 'Logo', description: 'Your logo on documents and in the application.' },
    property: { title: 'Property address', description: 'Address and check-in / check-out hours.' },
    taxes: {
      title: 'Taxes',
      description: 'At least one active tax rule — or no tax at all: skip the step.',
    },
    billing: {
      title: 'Billing',
      description: 'Numbering, deadlines and wording of proformas and invoices.',
    },
    resources: { title: 'Rooms and resources', description: 'At least one bookable resource.' },
    ratePlans: { title: 'Rates', description: 'At least one active rate on a resource.' },
    compliance: {
      title: 'Mandatory documents',
      description: 'Documents required at check-in (ID card, passport, police form).',
    },
    outlets: { title: 'Outlets', description: 'Restaurant, bar, shop…' },
    catalog: { title: 'Menu', description: 'At least one active product on the menu.' },
    printing: {
      title: 'Printing',
      description: 'One print destination (kitchen, bar, till) — or none: skip.',
    },
    users: { title: 'Team', description: 'Invite at least one colleague.' },
  },
  card: { title: 'Getting started', open: 'Open the assistant' },
  toasts: { updated: 'Assistant updated.' },
};

/** My subscription, tenant side (phase H8, ADR-023, D-H52). */
export const subscription = {
  title: 'Subscription',
  subtitle: 'Your HOREVIA subscription: plan, period, limits, invoices and how to pay.',
  plan: 'Plan',
  status: 'Status',
  mode: 'Billing',
  modes: { MANUAL: 'Off-platform', INVOICED: 'Invoiced by HOREVIA' },
  price: 'Price',
  perPeriod: { MONTHLY: '/ month', YEARLY: '/ year' },
  currentPeriod: 'Current period',
  nextInvoice: 'Next invoice',
  trialEnds: 'Trial ends',
  ends: 'Subscription ends',
  since: 'Since {{date}}',
  none: 'No active subscription. Contact HOREVIA.',
  pastDue: 'An invoice is past due since {{date}}.',
  suspension: 'Without payment, access will be suspended on {{date}}.',
  limits: {
    title: 'Plan limits',
    properties: 'Properties',
    resources: 'Resources',
    users: 'Accounts',
    unlimited: 'unlimited',
    usage: '{{used}} / {{max}}',
    hint: 'Limits apply when creating: existing items are never blocked.',
  },
  invoices: {
    title: 'Subscription invoices',
    none: 'No invoice: your subscription is not invoiced through the platform.',
    download: 'PDF',
    columns: {
      number: 'Number',
      period: 'Period',
      issued: 'Issued',
      due: 'Due',
      total: 'Total',
      balance: 'Balance',
      status: 'Status',
    },
    status: {
      ISSUED: 'To pay',
      PARTIALLY_PAID: 'Partially paid',
      PAID: 'Paid',
      CANCELLED: 'Cancelled',
    },
    overdue: 'Overdue',
    payments: 'Payments received',
  },
  payment: {
    title: 'How to pay',
    issuer: 'Beneficiary',
    bank: 'Bank',
    account: 'Account',
    iban: 'IBAN',
    swift: 'SWIFT',
    methods: 'Accepted methods',
    terms: 'Terms',
    contact: 'Contact',
    reference: 'Quote the invoice number and your customer id “{{slug}}” when paying.',
  },
  banner: {
    TRIAL_ENDING: 'Your HOREVIA trial ends on {{date}}.',
    PAST_DUE: 'Subscription invoice {{number}} is past due since {{date}}: {{balance}} remaining.',
    SUSPENSION_SCHEDULED:
      'Invoice {{number}} unpaid ({{balance}}): without payment, access will be suspended on {{date}}.',
    ENDING: 'Your HOREVIA subscription ends on {{date}}.',
    open: 'View my subscription',
  },
};

export const insights = {
  title: 'Analytics',
  overview: {
    title: 'Consolidated view',
    subtitle:
      'Accommodation, restaurant and bar revenue over the period — every sale counted once.',
  },
  sales: {
    title: 'Sales',
    subtitle:
      'Paid and stay-charged orders by outlet, category, product, hour, weekday, server and service type.',
  },
  stock: {
    title: 'Stock & margins',
    subtitle:
      'Consumption at stock issue, received purchases, losses, count variances and margin per product sold.',
  },
  modules: {
    ACCOMMODATION: 'Accommodation',
    RESTAURANT: 'Restaurant',
    BAR: 'Bar',
    ROOM_SERVICE: 'Room service',
    SHOP: 'Shop',
    OTHER: 'Other sales',
  },
  kpi: {
    revenue: 'Consolidated revenue',
    salesRevenue: 'Sales revenue',
    revenueFoot: 'over {{count}} day',
    revenueFoot_other: 'over {{count}} days',
    collected: 'Collected',
    collectedFoot: 'all modules, refunds deducted',
    expenses: 'Expenses',
    expensesFoot: 'disbursements of the period',
    costOfSales: 'Cost of sales',
    costOfSalesFoot: 'stock issues at average cost',
    grossMargin: 'Gross margin',
    grossMarginFoot: 'sales excl. tax − cost',
    grossMarginRate: 'Margin rate',
    orders: 'Orders',
    ordersFoot: 'paid or charged to a stay',
    covers: 'Covers',
    averageTicket: 'Average ticket',
    happyHour: 'Happy hour discounts',
    transferred: 'Charged to stays',
    transferredFoot: '{{count}} order',
    transferredFoot_other: '{{count}} orders',
    cancelled: 'Cancelled',
    occupancy: 'Occupancy rate',
    adr: 'Average daily rate',
    revpar: 'RevPAR',
    stays: 'Stays',
    consumedCost: 'Consumed',
    consumedFoot: 'stock issued on sales',
    purchasedCost: 'Purchases received',
    lostCost: 'Losses',
    countVariance: 'Count variances',
    stockValue: 'Stock value',
    stockValueFoot: 'at period end, average cost',
    revenueExclTax: 'Revenue excl. tax (known cost)',
  },
  sections: {
    hotel: 'Accommodation',
    pos: 'Outlets',
    finance: 'Collections & charges',
    byModule: 'Split by family',
    daily: 'Revenue per day and family',
    topOutlets: 'Top outlets',
    topProducts: 'Top products',
    collectedByMethod: 'Collections by method',
    paymentsByMethod: 'Sales collected by method',
    byItem: 'By item',
    byCategory: 'By category',
    margins: 'Margins by product',
  },
  charts: {
    dailyDescription:
      'Revenue per day from {{from}} to {{to}}, stacked by family: {{total}} in total. Figures are available in the “Show data” table.',
    byModuleDescription:
      'Share of each family in the consolidated revenue of the period ({{count}} families).',
    breakdownDescription: 'Sales revenue by {{axis}} over the period ({{count}} rows).',
    showData: 'Show data',
    thousands: 'in thousands of {{currency}}',
    thousandsShort: 'k{{currency}}',
    columns: {
      date: 'Day',
      module: 'Family',
      amount: 'Amount',
      outlet: 'Outlet',
      product: 'Product',
      quantity: 'Quantity',
      orders: 'Orders',
      method: 'Method',
      count: 'Operations',
    },
  },
  axes: {
    OUTLET: 'Outlets',
    CATEGORY: 'Categories',
    PRODUCT: 'Products',
    HOUR: 'Hours',
    WEEKDAY: 'Weekdays',
    SERVER: 'Servers',
    SERVICE_TYPE: 'Service types',
  },
  axisSingular: {
    OUTLET: 'outlet',
    CATEGORY: 'category',
    PRODUCT: 'product',
    HOUR: 'hour',
    WEEKDAY: 'weekday',
    SERVER: 'server',
    SERVICE_TYPE: 'service type',
  },
  weekdays: {
    '1': 'Monday',
    '2': 'Tuesday',
    '3': 'Wednesday',
    '4': 'Thursday',
    '5': 'Friday',
    '6': 'Saturday',
    '7': 'Sunday',
  },
  serviceTypes: {
    DINE_IN: 'Dine in',
    COUNTER: 'Counter',
    TAKEAWAY: 'Takeaway',
    DELIVERY: 'Delivery',
    ROOM_SERVICE: 'Room service',
  },
  methods: {
    CASH: 'Cash',
    MOBILE_MONEY: 'Mobile money',
    BANK_TRANSFER: 'Bank transfer',
    CHEQUE: 'Cheque',
    CARD: 'Card',
    OTHER: 'Other',
  },
  table: {
    label: 'Label',
    orders: 'Orders',
    covers: 'Covers',
    quantity: 'Quantity',
    revenue: 'Revenue',
    share: 'Share',
    averageTicket: 'Average ticket',
    happyHour: 'Happy hour',
    item: 'Item',
    category: 'Category',
    unit: 'Unit',
    consumed: 'Consumed',
    consumedCost: 'Consumed cost',
    purchased: 'Purchased',
    purchasedCost: 'Purchase cost',
    lost: 'Losses',
    lostCost: 'Loss cost',
    variance: 'Count variance',
    product: 'Product',
    revenueExclTax: 'Revenue excl. tax',
    cost: 'Cost',
    margin: 'Margin',
    marginRate: 'Rate',
    costUnknown: 'Unknown cost',
    costUnknownHint: 'This product has no stock recipe: its margin cannot be computed.',
    noServer: 'No server',
    noCategory: 'No category',
    caption: '{{axis}} — {{count}} rows',
  },
  filters: {
    outlet: 'Outlet',
    allOutlets: 'All outlets',
    axis: 'Analysis axis',
  },
  actions: {
    exportCsv: 'Export CSV',
    openReport: 'Open the report',
    openSales: 'See sales',
    openStock: 'See stock',
    openAnalytics: 'Open Analytics',
  },
  empty: {
    title: 'No sales over this period',
    body: 'Widen the period or switch outlet.',
    stockTitle: 'No stock movement over this period',
    stockBody: 'No consumption, purchase or loss is recorded on these dates.',
  },
  noModule: {
    pos: 'The POS module is not active: no sales to analyse.',
    inventory: 'The Inventory module is not active: consumption and margins are unavailable.',
    analytics:
      'Horevia Analytics is not enabled for your organisation. Enable it from the modules.',
  },
  financeHidden:
    'Collected amounts, expenses, costs and margins require the “Financial reports” permission.',
  dashboard: {
    salesTitle: 'Today’s sales',
    salesOrders: '{{count}} paid order',
    salesOrders_other: '{{count}} paid orders',
    salesOpen: '{{count}} open',
    salesAverage: 'average ticket {{amount}}',
    salesEmpty: 'No paid sale today.',
    consolidatedTitle: 'Consolidated view of the month',
    consolidatedFoot: 'from {{from}} to {{to}}',
  },
};

export default {
  common,
  nav,
  auth,
  dashboard,
  analytics,
  search,
  reports,
  customers,
  reservations,
  users,
  roles,
  audit,
  settings,
  profile,
  resources,
  calendar,
  availability,
  maintenance,
  errors,
  documents,
  compliance,
  housekeeping,
  tasks,
  billing,
  payments,
  cash,
  expenses,
  taxes,
  pos,
  stock,
  platform,
  support,
  onboarding,
  subscription,
  insights,
  crm,
};
